import React, { useEffect, useState, useRef, useCallback } from 'react';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { auth } from '../../../../firebase/config';
import { useAppSelector } from '../../../../store';
import Button from '../../../../components/Button';
import TextField from '../../../../components/TextField';
import Toast from '../../../../components/Toast';
import DotLoader from '../../../../components/DotLoader';
import { FiPlus, FiSearch, FiEye, FiAlertCircle, FiFileText, FiMail, FiDownload, FiEdit2, FiSave, FiX, FiCalendar, FiClipboard, FiRefreshCw, FiChevronDown, FiCheck } from 'react-icons/fi';
import Modal from '../../../../components/Modal';
import Icon from '../../../../components/Icon';
import {
    fetchSurveys,
    fetchComplaints,
    fetchSurveysPage,
    fetchAllSurveysForExport,
    updateSurvey,
    updateComplaint,
    getCompanyId,
    createSurvey,
    type Survey,
    type Complaint,
    type SurveyPageCursor,
    type SurveyStatusFilter
} from '../service/surveyService';
import { db } from '../../../../firebase/config';
import { getDocs, collection, addDoc, serverTimestamp, query, where, doc, updateDoc, getDoc } from 'firebase/firestore';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { sendEmailsWithErrorHandling, validateEmails, formatSurveyEmailBody, formatComplaintEmailBody } from '../../../../api/emailService';
import Table, { Column } from '../../../../components/Table';

const LARGE_EXPORT_MIN_ROWS = 100;
/** When search or any filter is active, PDF table is capped for readability; full list when scope is wide open. */
const PDF_FILTERED_ROW_CAP = 45;

/** Load remote bank logo for jsPDF (CORS must allow canvas read). */
function tryLoadImageForPdf(url: string): Promise<{ dataUrl: string; format: 'PNG'; w: number; h: number } | null> {
    if (!url || typeof url !== 'string') return Promise.resolve(null);
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    resolve(null);
                    return;
                }
                ctx.drawImage(img, 0, 0);
                resolve({
                    dataUrl: canvas.toDataURL('image/png'),
                    format: 'PNG',
                    w: img.naturalWidth,
                    h: img.naturalHeight,
                });
            } catch {
                resolve(null);
            }
        };
        img.onerror = () => resolve(null);
        img.src = url;
    });
}

function normalizeDisplayStatus(status?: string): string {
    const t = String(status || '').trim();
    if (!t || t.toLowerCase() === 'n/a' || t.toLowerCase() === 'na') return 'Pending';
    return t;
}

function startOfLocalDayYmd(ymd: string): Date {
    const [y, m, d] = ymd.split('-').map((n) => parseInt(n, 10));
    return new Date(y, m - 1, d, 0, 0, 0, 0);
}

function endOfLocalDayYmd(ymd: string): Date {
    const [y, m, d] = ymd.split('-').map((n) => parseInt(n, 10));
    return new Date(y, m - 1, d, 23, 59, 59, 999);
}

function collectSurveyIssueLabels(s: Survey): string[] {
    const out: string[] = [];
    const push = (arr?: string[]) => {
        (arr || []).forEach((x) => {
            const v = String(x || '').trim();
            if (v) out.push(v);
        });
    };
    const any = s as any;
    push(any.cctv?.issues);
    push(any.cctv?.otherIssues);
    push(any.bams?.issues);
    push(any.bams?.otherIssues);
    push(any.guarding?.issues);
    push(any.guarding?.otherIssues);
    push(any.otherAssessments?.otherIssues);
    return out;
}

function pickTopFromCountMap(m: Map<string, number>): string {
    let best = '';
    let n = 0;
    m.forEach((v, k) => {
        if (v > n) {
            n = v;
            best = k;
        }
    });
    return best || '—';
}

type BranchMetricRow = {
    branchCode: string;
    /** Most common location or branch name (street address is in `address`) */
    locationOrBranch: string;
    /** Dominant street / site address for this branch code */
    address: string;
    totalVisits: number;
    pendingVisits: number;
    resolvedVisits: number;
    jointVisitYes: number;
    majorityIssue: string;
};

function branchLocationNameLabel(branch: Survey['branch']): string {
    const b = branch as any;
    const s = String(b?.locationName || branch?.branchName || '').trim();
    return s || '—';
}

function branchAddressOnly(branch: unknown): string {
    const a = String((branch as any)?.address || '').trim();
    return a || '—';
}

function buildBranchMetricsFromSurveys(rows: Survey[]): BranchMetricRow[] {
    type Agg = {
        total: number;
        pending: number;
        resolved: number;
        jointYes: number;
        issues: Map<string, number>;
        locationLabels: Map<string, number>;
        addressLabels: Map<string, number>;
    };
    const map = new Map<string, Agg>();
    for (const s of rows) {
        const code = String(s.branch?.branchCode || '—');
        if (!map.has(code)) {
            map.set(code, {
                total: 0,
                pending: 0,
                resolved: 0,
                jointYes: 0,
                issues: new Map(),
                locationLabels: new Map(),
                addressLabels: new Map(),
            });
        }
        const a = map.get(code)!;
        a.total += 1;
        const disp = normalizeDisplayStatus(s.status);
        if (disp === 'Pending') a.pending += 1;
        if (disp === 'Resolved') a.resolved += 1;
        if (String(s.jointVisit || '').trim().toLowerCase() === 'yes') a.jointYes += 1;
        for (const lab of collectSurveyIssueLabels(s)) {
            a.issues.set(lab, (a.issues.get(lab) || 0) + 1);
        }
        const locLabel = branchLocationNameLabel(s.branch);
        a.locationLabels.set(locLabel, (a.locationLabels.get(locLabel) || 0) + 1);
        const addrLabel = branchAddressOnly(s.branch);
        a.addressLabels.set(addrLabel, (a.addressLabels.get(addrLabel) || 0) + 1);
    }
    return Array.from(map.entries())
        .map(([branchCode, a]) => ({
            branchCode,
            locationOrBranch: pickTopFromCountMap(a.locationLabels),
            address: pickTopFromCountMap(a.addressLabels),
            totalVisits: a.total,
            pendingVisits: a.pending,
            resolvedVisits: a.resolved,
            jointVisitYes: a.jointYes,
            majorityIssue: pickTopFromCountMap(a.issues),
        }))
        .sort((x, y) => y.totalVisits - x.totalVisits);
}

function buildBranchMetricsFromComplaints(rows: Complaint[]): BranchMetricRow[] {
    type Agg = {
        total: number;
        pending: number;
        resolved: number;
        jointYes: number;
        issues: Map<string, number>;
        locationLabels: Map<string, number>;
        addressLabels: Map<string, number>;
    };
    const map = new Map<string, Agg>();
    for (const c of rows) {
        const code = String(c.surveyData?.branch?.branchCode || '—');
        if (!map.has(code)) {
            map.set(code, {
                total: 0,
                pending: 0,
                resolved: 0,
                jointYes: 0,
                issues: new Map(),
                locationLabels: new Map(),
                addressLabels: new Map(),
            });
        }
        const a = map.get(code)!;
        a.total += 1;
        const disp = normalizeDisplayStatus(c.status);
        if (disp === 'Pending') a.pending += 1;
        if (disp === 'Resolved') a.resolved += 1;
        if (String(c.surveyData?.jointVisit || '').trim().toLowerCase() === 'yes') a.jointYes += 1;
        const it = String(c.issueType || 'Other').trim() || 'Other';
        a.issues.set(it, (a.issues.get(it) || 0) + 1);
        const br = c.surveyData?.branch;
        const locLabel = branchLocationNameLabel(br as Survey['branch']);
        a.locationLabels.set(locLabel, (a.locationLabels.get(locLabel) || 0) + 1);
        const addrLabel = branchAddressOnly(br);
        a.addressLabels.set(addrLabel, (a.addressLabels.get(addrLabel) || 0) + 1);
    }
    return Array.from(map.entries())
        .map(([branchCode, a]) => ({
            branchCode,
            locationOrBranch: pickTopFromCountMap(a.locationLabels),
            address: pickTopFromCountMap(a.addressLabels),
            totalVisits: a.total,
            pendingVisits: a.pending,
            resolvedVisits: a.resolved,
            jointVisitYes: a.jointYes,
            majorityIssue: pickTopFromCountMap(a.issues),
        }))
        .sort((x, y) => y.totalVisits - x.totalVisits);
}

function isExportWideOpen(opts: {
    appliedSearchTerm: string;
    dateFilter: 'all' | 'daily' | 'weekly' | 'monthly';
    surveyCreatedFrom: string | null;
    surveyCreatedTo: string | null;
    statusFilter: SurveyStatusFilter;
    psoFilter: string;
    locationFilter: string;
    canUsePsoLocationFilters: boolean;
}): boolean {
    if (opts.appliedSearchTerm.trim()) return false;
    if (opts.surveyCreatedFrom && opts.surveyCreatedTo) return false;
    if (opts.dateFilter !== 'all') return false;
    if (opts.statusFilter !== 'all') return false;
    if (opts.canUsePsoLocationFilters && (opts.psoFilter !== 'all' || opts.locationFilter !== 'all')) return false;
    return true;
}

function summarizeStatusesFromSurveys(rows: Survey[]): string {
    const m = new Map<string, number>();
    for (const s of rows) {
        const k = normalizeDisplayStatus(s.status);
        m.set(k, (m.get(k) || 0) + 1);
    }
    return Array.from(m.entries())
        .sort((a, b) => b[1] - a[1])
        .map(([k, v]) => `${k}: ${v}`)
        .join('  ·  ');
}

function summarizeStatusesFromComplaints(rows: Complaint[]): string {
    const m = new Map<string, number>();
    for (const c of rows) {
        const k = normalizeDisplayStatus(c.status);
        m.set(k, (m.get(k) || 0) + 1);
    }
    return Array.from(m.entries())
        .sort((a, b) => b[1] - a[1])
        .map(([k, v]) => `${k}: ${v}`)
        .join('  ·  ');
}

function matchesStatusFilterValue(statusFilter: SurveyStatusFilter, raw: any): boolean {
    const normalized = String(raw || '').trim().toLowerCase();
    if (statusFilter === 'all') return true;
    if (statusFilter === 'pending') {
        return normalized === '' || normalized === 'pending' || normalized === 'n/a' || normalized === 'na';
    }
    return normalized === statusFilter;
}

function filterComplaintsList(
    list: Complaint[],
    opts: {
        appliedSearchTerm: string;
        dateFilter: 'all' | 'daily' | 'weekly' | 'monthly';
        surveyCreatedFrom: string | null;
        surveyCreatedTo: string | null;
        psoFilter: string;
        locationFilter: string;
        statusFilter: SurveyStatusFilter;
        canUsePsoLocationFilters: boolean;
    }
): Complaint[] {
    let next = list;

    if (opts.appliedSearchTerm) {
        const term = opts.appliedSearchTerm.toLowerCase();
        next = next.filter((complaint) => {
            const complaintId = (complaint.complaintId || '').toString().toLowerCase();
            const issueType = (complaint.issueType || '').toString().toLowerCase();
            const surveyId = (complaint.surveyData?.surveyId || '').toString().toLowerCase();
            const branchCode = (complaint.surveyData?.branch?.branchCode || '').toString().toLowerCase();
            const locationName = (complaint.surveyData?.branch?.locationName || '').toString().toLowerCase();
            const psoName = ((complaint.surveyData as any)?.createdByName || '').toString().toLowerCase();
            const status = (complaint.status || '').toString().toLowerCase();
            return (
                complaintId.includes(term) ||
                issueType.includes(term) ||
                surveyId.includes(term) ||
                branchCode.includes(term) ||
                locationName.includes(term) ||
                psoName.includes(term) ||
                status.includes(term)
            );
        });
    }

    // Convert YYYY-MM-DD string range or presets to bounds
    let dateOfVisitStart: string | null = null;
    let dateOfVisitEnd: string | null = null;

    if (opts.surveyCreatedFrom && opts.surveyCreatedTo) {
        dateOfVisitStart = opts.surveyCreatedFrom;
        dateOfVisitEnd = opts.surveyCreatedTo;
    } else if (opts.dateFilter !== 'all') {
        const todayStr = dayjs().format('YYYY-MM-DD');
        if (opts.dateFilter === 'daily') {
            dateOfVisitStart = todayStr;
            dateOfVisitEnd = todayStr;
        } else if (opts.dateFilter === 'weekly') {
            dateOfVisitStart = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
            dateOfVisitEnd = todayStr;
        } else if (opts.dateFilter === 'monthly') {
            dateOfVisitStart = dayjs().subtract(30, 'day').format('YYYY-MM-DD');
            dateOfVisitEnd = todayStr;
        }
    }

    if (dateOfVisitStart && dateOfVisitEnd) {
        next = next.filter((complaint) => {
            const dov = complaint.surveyData?.dateOfVisit;
            return dov && dov >= dateOfVisitStart! && dov <= dateOfVisitEnd!;
        });
    }

    next = next.filter((c) => matchesStatusFilterValue(opts.statusFilter, c.status));

    if (opts.canUsePsoLocationFilters) {
        if (opts.psoFilter !== 'all') {
            next = next.filter((c) => c.surveyData?.createdBy === opts.psoFilter);
        }
        if (opts.locationFilter !== 'all') {
            next = next.filter((c) => c.surveyData?.locationId === opts.locationFilter);
        }
    }

    next.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
        return dateB - dateA;
    });

    return next;
}

export default function Surveys() {
    const [activeTab, setActiveTab] = useState<'surveys' | 'complaints'>('surveys');
    const [surveys, setSurveys] = useState<Survey[]>([]);
    const [complaints, setComplaints] = useState<Complaint[]>([]);
    const [filteredSurveys, setFilteredSurveys] = useState<Survey[]>([]);
    const [filteredComplaints, setFilteredComplaints] = useState<Complaint[]>([]);
    const [searchInput, setSearchInput] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
    const [psoFilter, setPsoFilter] = useState('all');
    const [locationFilter, setLocationFilter] = useState('all');
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [draftDateFilter, setDraftDateFilter] = useState<'all' | 'daily' | 'weekly' | 'monthly'>('all');
    const [draftPsoFilter, setDraftPsoFilter] = useState('all');
    const [draftLocationFilter, setDraftLocationFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState<SurveyStatusFilter>('all');
    const [draftStatusFilter, setDraftStatusFilter] = useState<SurveyStatusFilter>('all');
    const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' | 'warning' }>({
        show: false,
        message: '',
        type: 'success',
    });
    const [loading, setLoading] = useState(true);
    const [loadingComplaints, setLoadingComplaints] = useState(false);
    const [selectedSurvey, setSelectedSurvey] = useState<Survey | null>(null);
    const [selectedComplaint, setSelectedComplaint] = useState<Complaint | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailRecipients, setEmailRecipients] = useState('');
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [dateFilter, setDateFilter] = useState<'all' | 'daily' | 'weekly' | 'monthly'>('all');
    /** Inclusive YYYY-MM-DD on survey `createdAt` (both required to apply). */
    const [surveyCreatedFrom, setSurveyCreatedFrom] = useState<string | null>(null);
    const [surveyCreatedTo, setSurveyCreatedTo] = useState<string | null>(null);
    const [draftSurveyCreatedFrom, setDraftSurveyCreatedFrom] = useState<string | null>(null);
    const [draftSurveyCreatedTo, setDraftSurveyCreatedTo] = useState<string | null>(null);
    const [rowSurveyStatusSavingId, setRowSurveyStatusSavingId] = useState<string | null>(null);
    const [rowComplaintStatusSavingId, setRowComplaintStatusSavingId] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editedSurvey, setEditedSurvey] = useState<Partial<Survey> | null>(null);
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [updating, setUpdating] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [branches, setBranches] = useState<any[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [branchSearchTerm, setBranchSearchTerm] = useState('');
    const [showBranchDropdown, setShowBranchDropdown] = useState(false);
    const [psoName, setPsoName] = useState<string>('');
    const [surveyExists, setSurveyExists] = useState(false);
    const [formData, setFormData] = useState({
        surveyId: '',
        dateOfVisit: new Date().toISOString().split('T')[0],
        branchId: '',
        // Flutter-aligned fields
        cctv: { status: 'Ok' as 'Ok' | 'Not OK', issues: [] as string[], otherIssues: [] as string[], remarks: '' },
        bams: { status: 'Ok' as 'Ok' | 'Not OK', issues: [] as string[], otherIssues: [] as string[], remarks: '' },
        guarding: { status: 'Ok' as 'Ok' | 'Not OK', issues: [] as string[], otherIssues: [] as string[], remarks: '' },
        otherAssessments: {
            status: 'Ok' as 'Ok' | 'Not OK',
            assessments: {
                emergencyExit: { selected: false, issues: [] as string[], remarks: '' },
                vantagePoint: { selected: false, issues: [] as string[], remarks: '' },
                cashCounterGlass: { selected: false, issues: [] as string[], remarks: '' },
                ironGrill: { selected: false, issues: [] as string[], remarks: '' },
                barbedWire: { selected: false, issues: [] as string[], remarks: '' },
                searchLight: { selected: false, issues: [] as string[], remarks: '' },
            },
            otherIssues: [] as string[],
            remarks: '',
        },
        jointVisit: 'No' as 'No' | 'Yes',
        jointTeams: [] as string[],
    });

    const uid = auth.currentUser?.uid;
    const userRole = sessionStorage.getItem('role'); // 'bank' or 'pso'
    const isBankUser = userRole === 'bank';
    const pso = useAppSelector((state: any) => state.user.pso);
    const bank = useAppSelector((state: any) => state.user.bank);
    const designation = (pso?.designation || '').replace(/_/g, ' ').toLowerCase();
    const isMISIncharge = designation.includes('mis');
    const isHead = designation === 'head' || designation === 'head pso';
    const showPsoLocationColumns = isBankUser || isMISIncharge || isHead;
    const isPsoRole = userRole === 'pso';
    /** Visit/complaint row status: bank, or field PSO (not MIS incharge / head). */
    const canEditRowStatus = isBankUser || (isPsoRole && !isMISIncharge && !isHead);
    const branchDropdownRef = useRef<HTMLDivElement>(null);
    const exportMenuRef = useRef<HTMLDivElement>(null);
    const exportCancelledRef = useRef(false);
    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const [exportProgress, setExportProgress] = useState<{
        active: boolean;
        format: 'excel' | 'pdf' | null;
        message: string;
        percent: number;
    }>({ active: false, format: null, message: '', percent: 0 });
    const [rowContextMenu, setRowContextMenu] = useState<
        | null
        | { kind: 'survey'; survey: Survey; clientX: number; clientY: number }
        | { kind: 'complaint'; complaint: Complaint; clientX: number; clientY: number }
    >(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 20;
    const [surveyPageCursors, setSurveyPageCursors] = useState<Record<number, SurveyPageCursor | null>>({});
    const [surveyTotalPages, setSurveyTotalPages] = useState(1);
    const surveyQueryCacheRef = useRef<Map<string, { rows: Survey[]; cursor: SurveyPageCursor | null; totalPages: number }>>(new Map());
    const surveysFetchSeqRef = useRef(0);
    const [psoFilterOptions, setPsoFilterOptions] = useState<{ id: string; name: string }[]>([]);
    const [locationFilterOptions, setLocationFilterOptions] = useState<{ id: string; name: string }[]>([]);

    const getSurveyCacheKey = (
        page: number,
        term: string,
        filter: 'all' | 'daily' | 'weekly' | 'monthly',
        selectedPso: string,
        selectedLocation: string,
        selectedStatus: SurveyStatusFilter,
        createdFrom: string | null,
        createdTo: string | null
    ) =>
        `${page}|${term.trim().toLowerCase()}|${filter}|${selectedPso}|${selectedLocation}|${selectedStatus}|${createdFrom || ''}|${createdTo || ''}|${isBankUser ? 'bank' : 'pso'}|${isMISIncharge ? 'mis' : 'nomis'}|${pso?.designation || ''}`;

    const clearSurveyQueryCache = () => {
        surveyQueryCacheRef.current.clear();
    };

    const canUsePsoLocationFilters = isBankUser || isMISIncharge || isHead;
    const hasActiveSurveyFilters =
        dateFilter !== 'all' ||
        !!(surveyCreatedFrom && surveyCreatedTo) ||
        statusFilter !== 'all' ||
        (canUsePsoLocationFilters && (psoFilter !== 'all' || locationFilter !== 'all'));

    // Reset page on tab/filter change
    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab, appliedSearchTerm, dateFilter, surveyCreatedFrom, surveyCreatedTo, psoFilter, locationFilter, statusFilter]);

    useEffect(() => {
        if (!rowContextMenu) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setRowContextMenu(null);
        };
        const onDown = (e: MouseEvent) => {
            const menu = document.getElementById('surveys-table-row-context');
            if (menu?.contains(e.target as Node)) return;
            setRowContextMenu(null);
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onDown);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('mousedown', onDown);
        };
    }, [rowContextMenu]);

    const applySearch = () => {
        setAppliedSearchTerm(searchInput.trim());
        setCurrentPage(1);
    };

    const openFilterModal = () => {
        setDraftDateFilter(dateFilter);
        setDraftPsoFilter(canUsePsoLocationFilters ? psoFilter : 'all');
        setDraftLocationFilter(canUsePsoLocationFilters ? locationFilter : 'all');
        setDraftStatusFilter(statusFilter);
        setDraftSurveyCreatedFrom(surveyCreatedFrom);
        setDraftSurveyCreatedTo(surveyCreatedTo);
        setShowFilterModal(true);
    };

    const applyFilters = () => {
        const useRange = !!(draftSurveyCreatedFrom && draftSurveyCreatedTo);
        if (useRange) {
            setDateFilter('all');
            setSurveyCreatedFrom(draftSurveyCreatedFrom);
            setSurveyCreatedTo(draftSurveyCreatedTo);
        } else {
            setSurveyCreatedFrom(null);
            setSurveyCreatedTo(null);
            setDateFilter(draftDateFilter);
        }
        setPsoFilter(canUsePsoLocationFilters ? draftPsoFilter : 'all');
        setLocationFilter(canUsePsoLocationFilters ? draftLocationFilter : 'all');
        setStatusFilter(draftStatusFilter);
        setCurrentPage(1);
        clearSurveyQueryCache();
        setShowFilterModal(false);
    };

    const clearFilters = () => {
        setDateFilter('all');
        setSurveyCreatedFrom(null);
        setSurveyCreatedTo(null);
        setPsoFilter('all');
        setLocationFilter('all');
        setStatusFilter('all');
        setDraftDateFilter('all');
        setDraftPsoFilter('all');
        setDraftLocationFilter('all');
        setDraftStatusFilter('all');
        setDraftSurveyCreatedFrom(null);
        setDraftSurveyCreatedTo(null);
        setCurrentPage(1);
        clearSurveyQueryCache();
    };



    const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
        setToast({ show: true, message, type });
    };

    const getStatusBadge = (status?: string) => {
        const display = normalizeDisplayStatus(status);
        const statusLower = display.toLowerCase();
        let bgColor = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';

        if (statusLower === 'resolved') {
            bgColor = 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
        } else if (statusLower === 'partial') {
            bgColor = 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
        } else if (statusLower === 'pending') {
            bgColor = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
        }

        return (
            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${bgColor}`}>
                {display}
            </span>
        );
    };

    const surveyColumns: Column<Survey>[] = [
        { header: 'Survey ID', accessor: 'surveyId', width: '14%', render: (s) => <span className="font-medium text-gray-900 dark:text-gray-100">{s.surveyId || '—'}</span> },
        {
            header: 'Branch',
            accessor: 'branch',
            width: '18%',
            render: (s) => {
                const addr = String((s.branch as any)?.address || '').trim();
                return (
                    <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">{s.branch?.branchCode || '—'}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{(s.branch?.branchName || '').toString() || '—'}</div>
                        {addr ? <div className="text-xs text-gray-500 dark:text-gray-400">{addr}</div> : null}
                    </div>
                );
            },
        },
        { header: 'Date of Visit', accessor: 'dateOfVisit', width: '12%', render: (s) => <span className="text-gray-600 dark:text-gray-400">{s.dateOfVisit || '—'}</span> },
        { header: 'Joint Visit', accessor: 'jointVisit', width: '9%', render: (s) => <span className="text-gray-900 dark:text-gray-100">{s.jointVisit || '—'}</span> },
        { header: 'Status', accessor: 'status', width: '11%', render: (s) => {
                if (!canEditRowStatus || normalizeDisplayStatus(s.status) === 'Resolved') {
                    return <div className="min-h-[2.25rem]">{getStatusBadge(s.status)}</div>;
                }
                const saving = rowSurveyStatusSavingId === s.id;
                return (
                    <div className="relative w-full max-w-[10rem] min-h-[2.25rem]">
                        {saving && (
                            <div
                                className="absolute inset-0 z-10 flex items-center justify-center rounded-lg border border-gray-200 bg-white/90 backdrop-blur-[1px] dark:border-gray-600 dark:bg-gray-900/90"
                                aria-busy="true"
                            >
                                <DotLoader />
                            </div>
                        )}
                        <select
                            disabled={saving || !companyId}
                            value={normalizeDisplayStatus(s.status)}
                            onChange={(e) => void handleSurveyStatusChange(s, e.target.value)}
                            className="w-full rounded-lg border border-gray-300 bg-white py-1.5 px-2 text-xs font-medium text-gray-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                        >
                            {normalizeDisplayStatus(s.status) === 'Partial' ? (
                                <option value="Partial">Partial</option>
                            ) : (
                                <option value="Pending">Pending</option>
                            )}
                            <option value="Resolved">Resolved</option>
                        </select>
                    </div>
                );
            } },
        ...(showPsoLocationColumns
            ? ([
                  {
                      header: 'PSO',
                      width: '11%',
                      render: (s: Survey) => (
                          <span className="font-medium text-gray-900 dark:text-gray-100">{(s as any).createdByName || '—'}</span>
                      ),
                  },
                  {
                      header: 'Location',
                      width: '11%',
                      render: (s: Survey) => (
                          <span className="text-gray-700 dark:text-gray-300">{String((s.branch as any)?.locationName || '—')}</span>
                      ),
                  },
              ] as Column<Survey>[])
            : []),
        {
            header: 'Actions',
            width: '10%',
            render: (s) => (
                <div className="flex items-center gap-1">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            openDetailsModal(s);
                        }}
                        className="rounded-lg p-1 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30 transition-colors"
                        title="View Details"
                    >
                        <Icon icon={FiEye} className="h-4.5 w-4.5" />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            openEmailModal(s);
                        }}
                        className="rounded-lg p-1 text-purple-600 hover:bg-purple-50 dark:text-purple-400 dark:hover:bg-purple-900/30 transition-colors"
                        title="Send Email"
                    >
                        <Icon icon={FiMail} className="h-4.5 w-4.5" />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            downloadItemPDF(s);
                        }}
                        className="rounded-lg p-1 text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/30 transition-colors"
                        title="Download PDF"
                    >
                        <Icon icon={FiDownload} className="h-4.5 w-4.5" />
                    </button>
                </div>
            )
        }
    ];

    const complaintColumns: Column<Complaint>[] = [
        { header: 'Complaint ID', accessor: 'complaintId', width: '12%', render: (c) => <span className="font-medium text-gray-900 dark:text-gray-100">{c.complaintId || c.id}</span> },
        { header: 'Issue Type', accessor: 'issueType', width: '11%', render: (c) => <span className="text-gray-900 dark:text-gray-100">{c.issueType || '—'}</span> },
        { header: 'Related Survey', accessor: 'surveyData', width: '11%', render: (c) => <span className="text-gray-600 dark:text-gray-400">{c.surveyData?.surveyId || '—'}</span> },
        {
            header: 'Issues',
            width: '10%',
            render: (c) => {
                const total = c.issues?.length || 0;
                const resolved = c.resolvedIssues?.length || 0;
                const pending = total > 0 ? total - resolved : 0;
                if (total === 0) return <span className="text-gray-400">—</span>;
                return (
                    <div className="flex flex-col gap-0.5 text-xs font-semibold">
                        {pending > 0 && <span className="text-orange-600 dark:text-orange-400">Pending {pending}</span>}
                        {resolved > 0 && <span className="text-emerald-600 dark:text-emerald-400">Resolved {resolved}</span>}
                        {pending === 0 && resolved === 0 && <span className="text-gray-500">0</span>}
                    </div>
                );
            }
        },
        {
            header: 'Branch',
            width: '16%',
            render: (c) => {
                const br = c.surveyData?.branch;
                const addr = String((br as any)?.address || '').trim();
                return (
                    <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">{br?.branchCode || '—'}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{(br?.branchName || '').toString() || '—'}</div>
                        {addr ? <div className="text-xs text-gray-500 dark:text-gray-400">{addr}</div> : null}
                    </div>
                );
            },
        },
        ...(showPsoLocationColumns
            ? ([
                  {
                      header: 'Location',
                      width: '11%',
                      render: (c: Complaint) => (
                          <span className="text-gray-700 dark:text-gray-300">
                              {String((c.surveyData?.branch as any)?.locationName || '—')}
                          </span>
                      ),
                  },
                  {
                      header: 'PSO',
                      width: '11%',
                      render: (c: Complaint) => (
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                              {String((c.surveyData as any)?.createdByName || '—')}
                          </span>
                      ),
                  },
              ] as Column<Complaint>[])
            : []),
        { header: 'Status', accessor: 'status', width: '11%', render: (c) => {
                if (!canEditRowStatus || normalizeDisplayStatus(c.status) === 'Resolved') {
                    return <div className="min-h-[2.25rem]">{getStatusBadge(c.status)}</div>;
                }
                const saving = rowComplaintStatusSavingId === c.id;
                return (
                    <div className="relative w-full max-w-[10rem] min-h-[2.25rem]">
                        {saving && (
                            <div
                                className="absolute inset-0 z-10 flex items-center justify-center rounded-lg border border-gray-200 bg-white/90 backdrop-blur-[1px] dark:border-gray-600 dark:bg-gray-900/90"
                                aria-busy="true"
                            >
                                <DotLoader />
                            </div>
                        )}
                        <select
                            disabled={saving || !companyId}
                            value={normalizeDisplayStatus(c.status)}
                            onChange={(e) => void handleComplaintStatusChange(c, e.target.value)}
                            className="w-full rounded-lg border border-gray-300 bg-white py-1.5 px-2 text-xs font-medium text-gray-900 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                        >
                            {normalizeDisplayStatus(c.status) === 'Partial' && <option value="Partial">Partial</option>}
                            <option value="Pending">Pending</option>
                            <option value="Resolved">Resolved</option>
                        </select>
                    </div>
                );
            } },
        {
            header: 'Actions',
            width: '10%',
            render: (c) => (
                <div className="flex items-center gap-1">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            openDetailsModal(c);
                        }}
                        className="rounded-lg p-1 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30 transition-colors"
                        title="View Details"
                    >
                        <Icon icon={FiEye} className="h-4.5 w-4.5" />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            openEmailModal(c);
                        }}
                        className="rounded-lg p-1 text-purple-600 hover:bg-purple-50 dark:text-purple-400 dark:hover:bg-purple-900/30 transition-colors"
                        title="Send Email"
                    >
                        <Icon icon={FiMail} className="h-4.5 w-4.5" />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            downloadItemPDF(c);
                        }}
                        className="rounded-lg p-1 text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/30 transition-colors"
                        title="Download PDF"
                    >
                        <Icon icon={FiDownload} className="h-4.5 w-4.5" />
                    </button>
                </div>
            )
        }
    ];

    const paginatedSurveys = filteredSurveys;
    const paginatedComplaints = filteredComplaints.slice((currentPage - 1) * perPage, currentPage * perPage);

    // Generate Survey ID
    const generateSurveyId = () => {
        if (!psoName || psoName.length < 2) {
            const timestamp = Date.now().toString().slice(-4);
            return `SR-XX-${timestamp}`;
        }
        const psoInitials = psoName.substring(0, 2).toUpperCase();
        const timestamp = Date.now().toString().slice(-4);
        return `SR-${psoInitials}-${timestamp}`;
    };

    // Get current PSO ID
    const currentPsoId = pso?.authUid || uid || '';

    // Filter branches based on search term and PSO ID match
    const filteredBranches = branches.filter((branch: any) => {
        // MIS incharge and bank users see all branches (no PSO filter)
        if (!isBankUser && !isMISIncharge) {
            // First filter by PSO ID - only show branches assigned to this PSO
            if (branch.psoId && currentPsoId && branch.psoId !== currentPsoId) {
                return false;
            }
        }

        // Then filter by search term
        if (branchSearchTerm) {
            const searchLower = branchSearchTerm.toLowerCase();
            const branchCode = (branch.branchCode || '').toLowerCase();
            const branchName = (branch.branchName || '').toLowerCase();
            const address = (branch.address || '').toLowerCase();
            return branchCode.includes(searchLower) || branchName.includes(searchLower) || address.includes(searchLower);
        }

        return true;
    });

    useEffect(() => {
        if (!uid) return;

        const loadData = async () => {
            const fetchSeq = ++surveysFetchSeqRef.current;
            setLoading(true);
            try {
                // Get companyId first
                const fetchedCompanyId = await getCompanyId(uid, isBankUser);
                if (fetchedCompanyId) {
                    setCompanyId(fetchedCompanyId);

                    // Fetch branches for the add survey modal
                    try {
                        const branchSnap = await getDocs(collection(db, "banks", fetchedCompanyId, "branch"));
                        const allBranches = branchSnap.docs.map(doc => ({
                            id: doc.id,
                            ...doc.data()
                        }));

                        // Filter branches by PSO ID if user is a PSO; MIS incharge sees all
                        const currentPsoId = pso?.authUid || uid || '';
                        const filteredBranchesList = (isBankUser || isMISIncharge)
                            ? allBranches
                            : allBranches.filter((branch: any) => {
                                // Show branches that match this PSO's ID
                                return branch.psoId === currentPsoId;
                            });

                        setBranches(filteredBranchesList);

                        // Background migration for survey documents missing locationId or createdByName
                        void (async () => {
                            try {
                                const visitRef = collection(db, "banks", fetchedCompanyId, "visit");
                                const snap = await getDocs(visitRef);
                                let count = 0;
                                for (const d of snap.docs) {
                                    const sData = d.data();
                                    const needsLoc = !sData.locationId && sData.branchId;
                                    const needsPsoName = !sData.createdByName && sData.createdBy;
                                    if (needsLoc || needsPsoName) {
                                        const updates: any = {};
                                        if (needsLoc) {
                                            const br = allBranches.find(b => b.id === sData.branchId) as any;
                                            if (br && br.locationId) {
                                                updates.locationId = br.locationId;
                                            }
                                        }
                                        if (needsPsoName) {
                                            try {
                                                const psoDoc = await getDoc(doc(db, "banks", fetchedCompanyId, "pos", String(sData.createdBy)));
                                                if (psoDoc.exists()) {
                                                    updates.createdByName = String((psoDoc.data() as any)?.name || '').trim();
                                                }
                                            } catch (err) {
                                                console.error("Migration error fetching PSO:", err);
                                            }
                                        }
                                        if (Object.keys(updates).length > 0) {
                                            await updateDoc(doc(db, "banks", fetchedCompanyId, "visit", d.id), updates);
                                            count++;
                                        }
                                    }
                                }
                                if (count > 0) {
                                    console.log(`[Migration] Updated ${count} surveys with missing locationId/createdByName.`);
                                    clearSurveyQueryCache();
                                }
                            } catch (e) {
                                console.error("Background migration failed:", e);
                            }
                        })();
                    } catch (error) {
                        console.error('Error fetching branches:', error);
                    }

                    // Fetch full PSO + Location options for dropdown filters (not page-limited)
                    try {
                        const [psoSnap, locationSnap] = await Promise.all([
                            getDocs(collection(db, "banks", fetchedCompanyId, "pos")),
                            getDocs(collection(db, "locations")),
                        ]);
                        
                        const psoList = psoSnap.docs
                            .filter((d) => {
                                const designation = String((d.data() as any)?.designation || '').trim().toLowerCase();
                                return !designation || designation === 'pso';
                            })
                            .map((d) => ({
                                id: d.id,
                                name: String((d.data() as any)?.name || '').trim()
                            }))
                            .filter(x => x.name)
                            .sort((a, b) => a.name.localeCompare(b.name));

                        const locationList = locationSnap.docs
                            .map((d) => ({
                                id: d.id,
                                name: String((d.data() as any)?.name || '').trim()
                            }))
                            .filter(x => x.name)
                            .sort((a, b) => a.name.localeCompare(b.name));

                        setPsoFilterOptions(psoList);
                        setLocationFilterOptions(locationList);
                    } catch (error) {
                        console.error('Error fetching PSO/location filter options:', error);
                    }
                }

                const surveysPage = await fetchSurveysPage({
                    isBankUser: isBankUser || isMISIncharge,
                    psoDesignation: pso?.designation,
                    pageSize: perPage,
                    searchTerm: appliedSearchTerm,
                    selectedPsoId: psoFilter,
                    selectedLocationId: locationFilter,
                    dateFilter,
                    createdFromYmd: surveyCreatedFrom,
                    createdToYmd: surveyCreatedTo,
                    statusFilter,
                    cursor: null,
                });

                if (fetchSeq !== surveysFetchSeqRef.current) return;

                setSurveys(surveysPage.surveys);
                setFilteredSurveys(surveysPage.surveys);
                setSurveyPageCursors({ 1: surveysPage.cursor });
                setSurveyTotalPages(surveysPage.totalPages || 1);
                const key = getSurveyCacheKey(1, appliedSearchTerm, dateFilter, psoFilter, locationFilter, statusFilter, surveyCreatedFrom, surveyCreatedTo);
                surveyQueryCacheRef.current.set(key, {
                    rows: surveysPage.surveys,
                    cursor: surveysPage.cursor,
                    totalPages: surveysPage.totalPages || 1,
                });
            } catch (err: any) {
                if (fetchSeq !== surveysFetchSeqRef.current) return;
                showToast('Failed to load data', 'error');
                console.error(err);
            } finally {
                if (fetchSeq === surveysFetchSeqRef.current) {
                    setLoading(false);
                }
            }
        };

        loadData();
    }, [uid, isBankUser, isMISIncharge, isHead, pso?.designation, appliedSearchTerm, dateFilter, surveyCreatedFrom, surveyCreatedTo, psoFilter, locationFilter, statusFilter]);

    const handleSurveyStatusChange = async (survey: Survey, nextLabel: string) => {
        if (!canEditRowStatus) return;
        if (!companyId) {
            showToast('Company context not ready', 'error');
            return;
        }
        const value = nextLabel;
        setRowSurveyStatusSavingId(survey.id);
        try {
            await updateSurvey(companyId, survey.id, { status: value } as any);
            
            // If setting to Resolved, automatically resolve all complaints of this survey and their issues
            if (value === 'Resolved') {
                const surveyComplaints = complaints.filter(c => c.surveyId === survey.id);
                const complaintsToUpdate = surveyComplaints.filter(c => 
                    normalizeDisplayStatus(c.status) !== 'Resolved' || 
                    (c.issues && (!c.resolvedIssues || c.resolvedIssues.length !== c.issues.length))
                );
                if (complaintsToUpdate.length > 0) {
                    await Promise.all(complaintsToUpdate.map(c => {
                        const resolvedIssues = c.issues ? [...c.issues] : [];
                        return updateComplaint(companyId, survey.id, c.id, { status: 'Resolved', resolvedIssues } as any);
                    }));
                    
                    const updateList = (list: Complaint[]) => list.map(x => {
                        if (x.surveyId === survey.id && complaintsToUpdate.some(u => u.id === x.id)) {
                            return { ...x, status: 'Resolved', resolvedIssues: x.issues ? [...x.issues] : [] };
                        }
                        return x;
                    });
                    setComplaints(updateList);
                    setFilteredComplaints(updateList);
                    setSelectedComplaint(prev => prev && prev.surveyId === survey.id ? { ...prev, status: 'Resolved', resolvedIssues: prev.issues ? [...prev.issues] : [] } : prev);
                }
            }

            setSurveys((prev) => prev.map((x) => (x.id === survey.id ? { ...x, status: value } : x)));
            setFilteredSurveys((prev) => prev.map((x) => (x.id === survey.id ? { ...x, status: value } : x)));
            setSelectedSurvey((prev) => (prev && prev.id === survey.id ? { ...prev, status: value } : prev));
            showToast('Visit status updated', 'success');
        } catch (e) {
            console.error(e);
            showToast('Failed to update status', 'error');
        } finally {
            setRowSurveyStatusSavingId(null);
            clearSurveyQueryCache();
        }
    };

    const handleComplaintStatusChange = async (complaint: Complaint, nextLabel: string) => {
        if (!canEditRowStatus) return;
        if (!companyId || !complaint.surveyId) {
            showToast('Missing survey link for complaint', 'error');
            return;
        }
        const value = nextLabel;
        setRowComplaintStatusSavingId(complaint.id);
        
        const newResolvedIssues = value === 'Resolved' ? (complaint.issues ? [...complaint.issues] : []) : (value === 'Pending' ? [] : complaint.resolvedIssues || []);

        try {
            await updateComplaint(companyId, complaint.surveyId, complaint.id, { status: value, resolvedIssues: newResolvedIssues } as any);
            
            // Recalculate parent survey status based on complaints
            const updatedComplaints = complaints.map((x) => (x.id === complaint.id ? { ...x, status: value, resolvedIssues: newResolvedIssues } : x));
            const surveyComplaints = updatedComplaints.filter(c => c.surveyId === complaint.surveyId);
            
            const total = surveyComplaints.length;
            const resolvedCount = surveyComplaints.filter(c => normalizeDisplayStatus(c.status) === 'Resolved').length;
            
            let newSurveyStatus = 'Pending';
            if (total > 0) {
                if (resolvedCount === total) {
                    newSurveyStatus = 'Resolved';
                } else if (resolvedCount > 0) {
                    newSurveyStatus = 'Partial';
                } else if (surveyComplaints.some(c => normalizeDisplayStatus(c.status) === 'Partial')) {
                    newSurveyStatus = 'Partial';
                }
            } else {
                newSurveyStatus = 'Resolved';
            }
            
            const parentSurvey = surveys.find(s => s.id === complaint.surveyId);
            if (parentSurvey && parentSurvey.status !== newSurveyStatus) {
                await updateSurvey(companyId, complaint.surveyId, { status: newSurveyStatus } as any);
                setSurveys((prev) => prev.map((x) => (x.id === complaint.surveyId ? { ...x, status: newSurveyStatus } : x)));
                setFilteredSurveys((prev) => prev.map((x) => (x.id === complaint.surveyId ? { ...x, status: newSurveyStatus } : x)));
                if (selectedSurvey && selectedSurvey.id === complaint.surveyId) {
                    setSelectedSurvey((prev) => prev ? { ...prev, status: newSurveyStatus } : prev);
                }
            }

            setComplaints(updatedComplaints);
            setFilteredComplaints((prev) => prev.map((x) => (x.id === complaint.id ? { ...x, status: value, resolvedIssues: newResolvedIssues } : x)));
            setSelectedComplaint((prev) => (prev && prev.id === complaint.id ? { ...prev, status: value, resolvedIssues: newResolvedIssues } : prev));
            showToast('Complaint status updated', 'success');
        } catch (e) {
            console.error(e);
            showToast('Failed to update complaint status', 'error');
        } finally {
            setRowComplaintStatusSavingId(null);
            clearSurveyQueryCache();
        }
    };

    const handleIssueToggle = async (complaint: Complaint, issue: string, isChecked: boolean) => {
        if (!canEditRowStatus || !companyId || !complaint.surveyId) return;
        
        const currentResolved = complaint.resolvedIssues || [];
        let newResolved = isChecked 
            ? [...currentResolved, issue] 
            : currentResolved.filter(i => i !== issue);
            
        // remove duplicates just in case
        newResolved = Array.from(new Set(newResolved));
        
        const totalIssues = complaint.issues?.length || 0;
        let newStatus = 'Pending';
        if (totalIssues > 0) {
            if (newResolved.length === totalIssues) newStatus = 'Resolved';
            else if (newResolved.length > 0) newStatus = 'Partial';
        } else {
            newStatus = newResolved.length > 0 ? 'Resolved' : 'Pending';
        }

        setRowComplaintStatusSavingId(complaint.id);
        try {
            await updateComplaint(companyId, complaint.surveyId, complaint.id, { 
                status: newStatus, 
                resolvedIssues: newResolved 
            } as any);

            // Recalculate parent survey status based on complaints
            const updatedComplaints = complaints.map((x) => (x.id === complaint.id ? { ...x, status: newStatus, resolvedIssues: newResolved } : x));
            const surveyComplaints = updatedComplaints.filter(c => c.surveyId === complaint.surveyId);
            
            const total = surveyComplaints.length;
            const resolvedCount = surveyComplaints.filter(c => normalizeDisplayStatus(c.status) === 'Resolved').length;
            
            let newSurveyStatus = 'Pending';
            if (total > 0) {
                if (resolvedCount === total) {
                    newSurveyStatus = 'Resolved';
                } else if (resolvedCount > 0 || newStatus === 'Partial') {
                    newSurveyStatus = 'Partial';
                }
            } else {
                newSurveyStatus = 'Resolved';
            }
            
            const parentSurvey = surveys.find(s => s.id === complaint.surveyId);
            if (parentSurvey && parentSurvey.status !== newSurveyStatus) {
                await updateSurvey(companyId, complaint.surveyId, { status: newSurveyStatus } as any);
                setSurveys((prev) => prev.map((x) => (x.id === complaint.surveyId ? { ...x, status: newSurveyStatus } : x)));
                setFilteredSurveys((prev) => prev.map((x) => (x.id === complaint.surveyId ? { ...x, status: newSurveyStatus } : x)));
                if (selectedSurvey && selectedSurvey.id === complaint.surveyId) {
                    setSelectedSurvey((prev) => prev ? { ...prev, status: newSurveyStatus } : prev);
                }
            }

            setComplaints(updatedComplaints);
            setFilteredComplaints((prev) => prev.map((x) => (x.id === complaint.id ? { ...x, status: newStatus, resolvedIssues: newResolved } : x)));
            setSelectedComplaint((prev) => (prev && prev.id === complaint.id ? { ...prev, status: newStatus, resolvedIssues: newResolved } : prev));
        } catch (e) {
            console.error(e);
            showToast('Failed to update issue status', 'error');
        } finally {
            setRowComplaintStatusSavingId(null);
            clearSurveyQueryCache();
        }
    };
    useEffect(() => {
        const norm = (v: string) => (v || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        let list = surveys;

        if (appliedSearchTerm) {
            const termRaw = appliedSearchTerm.toLowerCase();
            const termNorm = norm(appliedSearchTerm);
            list = list.filter((survey) => {
                const surveyId = String(survey.surveyId || '');
                const branchCode = String(survey.branch?.branchCode || '');
                const branchName = String(survey.branch?.branchName || survey.branch?.address || '');
                return (
                    surveyId.toLowerCase().includes(termRaw) ||
                    branchCode.toLowerCase().includes(termRaw) ||
                    branchName.toLowerCase().includes(termRaw) ||
                    norm(surveyId).includes(termNorm) ||
                    norm(branchCode).includes(termNorm) ||
                    norm(branchName).includes(termNorm)
                );
            });
        }

        setFilteredSurveys(list);
    }, [surveys, appliedSearchTerm]);

    // Filter complaints (aligned with survey filter chips when bank user)
    useEffect(() => {
        setFilteredComplaints(
            filterComplaintsList(complaints, {
                appliedSearchTerm,
                dateFilter,
                surveyCreatedFrom,
                surveyCreatedTo,
                psoFilter,
                locationFilter,
                statusFilter,
                canUsePsoLocationFilters: canUsePsoLocationFilters,
            })
        );
    }, [appliedSearchTerm, complaints, dateFilter, surveyCreatedFrom, surveyCreatedTo, psoFilter, locationFilter, statusFilter, canUsePsoLocationFilters]);

    const loadSurveyPage = async (page: number) => {
        if (page === currentPage) return;
        const fetchSeq = ++surveysFetchSeqRef.current;
        setLoading(true);
        try {
            const cacheKey = getSurveyCacheKey(page, appliedSearchTerm, dateFilter, psoFilter, locationFilter, statusFilter, surveyCreatedFrom, surveyCreatedTo);
            const cached = surveyQueryCacheRef.current.get(cacheKey);
            if (cached) {
                if (fetchSeq !== surveysFetchSeqRef.current) return;
                setSurveys(cached.rows);
                setFilteredSurveys(cached.rows);
                setSurveyPageCursors((prev) => ({ ...prev, [page]: cached.cursor }));
                setSurveyTotalPages(cached.totalPages || 1);
                setCurrentPage(page);
                setLoading(false);
                return;
            }

            const cursor = page === 1 ? null : surveyPageCursors[page - 1] || null;
            const surveysPage = await fetchSurveysPage({
                isBankUser: isBankUser || isMISIncharge,
                psoDesignation: pso?.designation,
                pageSize: perPage,
                searchTerm: appliedSearchTerm,
                selectedPsoId: psoFilter,
                selectedLocationId: locationFilter,
                dateFilter,
                createdFromYmd: surveyCreatedFrom,
                createdToYmd: surveyCreatedTo,
                statusFilter,
                cursor,
            });

            if (fetchSeq !== surveysFetchSeqRef.current) return;

            setSurveys(surveysPage.surveys);
            setFilteredSurveys(surveysPage.surveys);
            setSurveyPageCursors((prev) => ({ ...prev, [page]: surveysPage.cursor }));
            setSurveyTotalPages(surveysPage.totalPages || 1);
            setCurrentPage(page);
            surveyQueryCacheRef.current.set(cacheKey, {
                rows: surveysPage.surveys,
                cursor: surveysPage.cursor,
                totalPages: surveysPage.totalPages || 1,
            });
        } catch (err) {
            if (fetchSeq !== surveysFetchSeqRef.current) return;
            console.error('Error loading surveys page:', err);
            showToast('Failed to load survey page', 'error');
        } finally {
            if (fetchSeq === surveysFetchSeqRef.current) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        if (activeTab !== 'complaints' && !showDetailsModal) return;
        if (complaints.length > 0) return;

        const loadComplaints = async () => {
            setLoadingComplaints(true);
            setLoading(true);
            try {
                const complaintsData = await fetchComplaints(isBankUser || isMISIncharge || isHead);
                setComplaints(complaintsData);
                setFilteredComplaints(complaintsData);
            } catch (err) {
                console.error('Error loading complaints:', err);
                showToast('Failed to load complaints', 'error');
            } finally {
                setLoading(false);
                setLoadingComplaints(false);
            }
        };

        void loadComplaints();
    }, [activeTab, showDetailsModal, complaints.length, isBankUser, isMISIncharge, isHead]);

    const openDetailsModal = (item: Survey | Complaint) => {
        if (activeTab === 'surveys') {
            setSelectedSurvey(item as Survey);
            setEditedSurvey(item as Survey);
            setIsEditing(false);
        } else {
            setSelectedComplaint(item as Complaint);
        }
        setShowDetailsModal(true);
    };

    const handleEdit = () => {
        if (!canEditRowStatus || isBankUser) return;
        if (selectedSurvey) {
            setIsEditing(true);
            setEditedSurvey({ ...selectedSurvey });
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        if (selectedSurvey) {
            setEditedSurvey(selectedSurvey);
        }
    };

    const resetForm = () => {
        const generatedId = generateSurveyId();
        setFormData({
            surveyId: generatedId,
            dateOfVisit: new Date().toISOString().split('T')[0],
            branchId: '',
            cctv: { status: 'Ok', issues: [], otherIssues: [], remarks: '' },
            bams: { status: 'Ok', issues: [], otherIssues: [], remarks: '' },
            guarding: { status: 'Ok', issues: [], otherIssues: [], remarks: '' },
            otherAssessments: {
                status: 'Ok',
                assessments: {
                    emergencyExit: { selected: false, issues: [], remarks: '' },
                    vantagePoint: { selected: false, issues: [], remarks: '' },
                    cashCounterGlass: { selected: false, issues: [], remarks: '' },
                    ironGrill: { selected: false, issues: [], remarks: '' },
                    barbedWire: { selected: false, issues: [], remarks: '' },
                    searchLight: { selected: false, issues: [], remarks: '' },
                },
                otherIssues: [],
                remarks: '',
            },
            jointVisit: 'No',
            jointTeams: [],
        });
        setBranchSearchTerm('');
        setShowBranchDropdown(false);
        setSurveyExists(false);
    };

    const cctvIssueOptions = ['Camera faulty', 'DVR/NVR issue', 'Display', 'Wiring issue', 'Recording days'];
    const bamsIssueOptions = ['Smoke', 'Panic', 'Door Contact', 'Response', 'SMS'];
    const guardingIssueOptions = ['Document', 'ID Card', 'Weapon', 'Appearance', 'Uniform', 'Shoes', 'Metal Detector'];
    const otherAssessmentLabels: Record<string, string> = {
        emergencyExit: 'Emergency Exit',
        vantagePoint: 'Vantage Point',
        cashCounterGlass: 'Cash Counter Glass Height',
        ironGrill: 'Iron Grill',
        barbedWire: 'Barbed Wire',
        searchLight: 'Search Light',
    };
    const otherAssessmentIssueOptions: Record<string, string[]> = {
        'Emergency Exit': ['Blocked', 'Locked', 'Signage missing', 'Light not working', 'Width insufficient', 'Height insufficient'],
        'Vantage Point': ['Poor visibility', 'Obstructed view', 'Height inadequate', 'Coverage incomplete', 'Lighting poor', 'Access difficult'],
        'Cash Counter Glass Height': ['Height too low', 'Height too high', 'Glass cracked', 'Glass dirty', 'Frame loose', 'Visibility poor'],
        'Iron Grill': ['Rusty', 'Loose', 'Missing bars', 'Paint peeling', 'Lock broken', 'Hinges damaged'],
        'Barbed Wire': ['Loose', 'Missing sections', 'Rusty', 'Height insufficient', 'Gaps present', 'Support weak'],
        'Search Light': ['Not working', 'Dim light', 'Coverage poor', 'Mounting loose', 'Wiring exposed', 'Switch faulty'],
    };
    const jointOptions = ['PSO', 'Regional Head', 'RHQ Team', 'REM Team', 'Facility Team', 'CCTV Team', 'BAMS Team', 'Guarding Team'];

    const toggleInList = (list: string[], value: string) => (list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);

    const addTag = (raw: string, current: string[]) => {
        const v = raw.trim();
        if (!v) return current;
        if (current.includes(v)) return current;
        return [...current, v];
    };

    // Check if a survey already exists for selected branch + date (avoid composite index by querying branchId only)
    useEffect(() => {
        const run = async () => {
            if (!showAddModal || !companyId) return;
            if (!formData.branchId || !formData.dateOfVisit) {
                setSurveyExists(false);
                return;
            }
            try {
                const q = query(
                    collection(db, 'banks', companyId, 'visit'),
                    where('branchId', '==', formData.branchId)
                );
                const snap = await getDocs(q);
                const exists = snap.docs.some((d) => String(d.data()?.dateOfVisit || '') === String(formData.dateOfVisit || ''));
                if (exists) {
                    showToast(
                        `⚠️ Survey already reported for ${branches.find((b) => b.id === formData.branchId)?.branchCode || 'this branch'} on ${formData.dateOfVisit}`,
                        'warning'
                    );
                    setSurveyExists(false);
                    setFormData((prev) => ({ ...prev, branchId: '' }));
                    setBranchSearchTerm('');
                    return;
                }
                setSurveyExists(false);
            } catch (e) {
                console.error('Error checking existing survey:', e);
                setSurveyExists(false);
            }
        };
        void run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showAddModal, companyId, formData.branchId, formData.dateOfVisit]);

    // Update PSO name when component loads
    useEffect(() => {
        if (pso?.name) {
            setPsoName(pso.name);
        } else {
            const storedName = sessionStorage.getItem('psoName');
            if (storedName) {
                setPsoName(storedName);
            }
        }
    }, [pso]);

    // Auto-generate survey ID when modal opens
    useEffect(() => {
        if (showAddModal && psoName) {
            const generatedId = generateSurveyId();
            setFormData(prev => ({ ...prev, surveyId: generatedId }));
        }
    }, [showAddModal, psoName]);

    // Close branch dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (branchDropdownRef.current && !branchDropdownRef.current.contains(event.target as Node)) {
                setShowBranchDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleAddSurvey = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!companyId) {
            showToast('Company ID not found', 'error');
            return;
        }
        if (!formData.branchId) {
            showToast('Please select a branch', 'error');
            return;
        }

        setIsSubmitting(true);
        try {
            const selectedBranch = branches.find(b => b.id === formData.branchId);
            const surveyData: Partial<Survey> = {
                surveyId: formData.surveyId,
                dateOfVisit: formData.dateOfVisit,
                branchId: formData.branchId,
                locationId: selectedBranch?.locationId || '',
                createdByName: psoName || '',
                locationName: selectedBranch?.locationName || '',
                companyId,
                createdBy: currentPsoId,
                cctv: {
                    status: formData.cctv.status,
                    issues: formData.cctv.issues,
                    otherIssues: formData.cctv.otherIssues,
                    remarks: formData.cctv.remarks,
                } as any,
                bams: {
                    status: formData.bams.status,
                    issues: formData.bams.issues,
                    otherIssues: formData.bams.otherIssues,
                    remarks: formData.bams.remarks,
                } as any,
                guarding: {
                    status: formData.guarding.status,
                    issues: formData.guarding.issues,
                    otherIssues: formData.guarding.otherIssues,
                    remarks: formData.guarding.remarks,
                } as any,
                otherAssessments: formData.otherAssessments as any,
                jointVisit: formData.jointVisit,
                jointTeams: formData.jointTeams,
                status: 'Pending',
                createdAt: serverTimestamp() as any,
            };

            const savedSurveyDocId = await createSurvey(companyId, surveyData);

            // Create complaints (same behavior as Flutter: when Not OK and has issues)
            let complaintIndex = 1;
            let anyComplaintsCreated = false;
            const createComplaint = async (issueType: string, issues: string[], otherIssues: string[], remarks: string) => {
                const all = [...issues, ...otherIssues].filter(Boolean);
                if (!all.length) return false;
                
                const formattedIndex = String(complaintIndex).padStart(2, '0');
                const complaintIdStr = `CM-${formData.surveyId}-${formattedIndex}`;
                complaintIndex++;
                
                await addDoc(collection(db, 'banks', companyId, 'visit', savedSurveyDocId, 'complain'), {
                    complaintId: complaintIdStr,
                    surveyId: savedSurveyDocId,
                    branchId: formData.branchId,
                    companyId,
                    issueType,
                    issues: all,
                    remarks: remarks || '',
                    status: 'Pending',
                    createdAt: serverTimestamp(),
                    createdBy: currentPsoId,
                });
                return true;
            };

            if (formData.cctv.status === 'Not OK') {
                if (await createComplaint('cctv', formData.cctv.issues, formData.cctv.otherIssues, formData.cctv.remarks)) anyComplaintsCreated = true;
            }
            if (formData.bams.status === 'Not OK') {
                if (await createComplaint('bams', formData.bams.issues, formData.bams.otherIssues, formData.bams.remarks)) anyComplaintsCreated = true;
            }
            if (formData.guarding.status === 'Not OK') {
                if (await createComplaint('guarding', formData.guarding.issues, formData.guarding.otherIssues, formData.guarding.remarks)) anyComplaintsCreated = true;
            }
            if (formData.otherAssessments.status === 'Not OK') {
                for (const [k, v] of Object.entries(formData.otherAssessments.assessments)) {
                    if (!v.selected) continue;
                    if (await createComplaint(k, v.issues, [], v.remarks)) anyComplaintsCreated = true;
                }
                if (formData.otherAssessments.otherIssues.length) {
                    if (await createComplaint('otherAssessment', [], formData.otherAssessments.otherIssues, formData.otherAssessments.remarks)) anyComplaintsCreated = true;
                }
            }
            
            if (!anyComplaintsCreated) {
                await updateSurvey(companyId, savedSurveyDocId, { status: 'Resolved' } as any);
            }

            // Refresh data
            const [surveysData, complaintsData] = await Promise.all([
                fetchSurveys(isBankUser),
                fetchComplaints(isBankUser || isHead)
            ]);

            setComplaints(complaintsData);

            // Recalculate status
            const calculateStatus = (surveyId: string): 'Pending' | 'Resolved' => {
                const surveyComplaints = complaintsData.filter(c => c.surveyId === surveyId);
                if (surveyComplaints.length === 0) {
                    return 'Resolved';
                }
                const pendingCount = surveyComplaints.filter(c => {
                    const status = c.status?.toLowerCase();
                    return status === 'pending' || !status;
                }).length;
                return pendingCount > 0 ? 'Pending' : 'Resolved';
            };

            const surveysWithStatus = surveysData.map(survey => ({
                ...survey,
                status: calculateStatus(survey.id)
            }));

            clearSurveyQueryCache();
            setSurveys(surveysWithStatus);
            setFilteredSurveys(surveysWithStatus);
            setFilteredComplaints(complaintsData);

            showToast('Survey created successfully', 'success');
            setShowAddModal(false);
            resetForm();
        } catch (error: any) {
            console.error('Error creating survey:', error);
            showToast('Failed to create survey', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSaveEdit = async () => {
        if (!canEditRowStatus || isBankUser) return;
        if (!selectedSurvey || !editedSurvey || !companyId) return;
        if (!isHead) {
            setUpdating(true);
            try {
                await updateSurvey(companyId, selectedSurvey.id, {
                    status: editedSurvey.status,
                    dateOfVisit: editedSurvey.dateOfVisit,
                    // Don't update jointVisit or jointTeams - they're read-only
                });

                // Refresh data
                const [surveysData, complaintsData] = await Promise.all([
                    fetchSurveys(isBankUser),
                    fetchComplaints(isBankUser || isHead)
                ]);

                setComplaints(complaintsData);

                // Recalculate status for each survey based on complaints
                const calculateStatus = (surveyId: string): 'Pending' | 'Resolved' => {
                    const surveyComplaints = complaintsData.filter(c => c.surveyId === surveyId);
                    if (surveyComplaints.length === 0) {
                        return 'Resolved';
                    }
                    const pendingCount = surveyComplaints.filter(c => {
                        const status = c.status?.toLowerCase();
                        return status === 'pending' || !status;
                    }).length;
                    return pendingCount > 0 ? 'Pending' : 'Resolved';
                };

                const surveysWithStatus = surveysData.map(survey => ({
                    ...survey,
                    status: calculateStatus(survey.id)
                }));

                clearSurveyQueryCache();
                setSurveys(surveysWithStatus);
                setFilteredSurveys(surveysWithStatus);

                // Update selected survey
                const updated = surveysWithStatus.find(s => s.id === selectedSurvey.id);
                if (updated) {
                    setSelectedSurvey(updated);
                    setEditedSurvey(updated);
                }

                setIsEditing(false);
                showToast('Survey updated successfully', 'success');
            } catch (err: any) {
                console.error('Error updating survey:', err);
                showToast('Failed to update survey', 'error');
            } finally {
                setUpdating(false);
            }
        }
    };

    const openEmailModal = (item: Survey | Complaint) => {
        if (activeTab === 'surveys') {
            setSelectedSurvey(item as Survey);
        } else {
            setSelectedComplaint(item as Complaint);
        }
        setEmailRecipients('');
        setShowEmailModal(true);
    };

    const handleSendEmail = async () => {
        const selectedItem = activeTab === 'surveys' ? selectedSurvey : selectedComplaint;
        if (!selectedItem) return;
        if (!emailRecipients.trim()) {
            showToast('Please enter at least one email address', 'warning');
            return;
        }

        // Validate email addresses
        const emailValidation = validateEmails(emailRecipients);
        if (!emailValidation.isValid) {
            showToast(`Invalid email addresses: ${emailValidation.invalid.join(', ')}`, 'error');
            return;
        }

        setIsSendingEmail(true);
        try {
            let emailData;
            if (activeTab === 'surveys') {
                const survey = selectedItem as Survey;
                // Get PSO name from sessionStorage or use default
                const psoName = sessionStorage.getItem('psoName') || 'PSO Team';
                const companyDetails = {
                    companyName: 'HBL Support',
                    imgUrl: ''
                };
                emailData = {
                    to: emailValidation.valid,
                    subject: `HBL Support - Survey Report - ${survey.surveyId || 'N/A'}`,
                    body: formatSurveyEmailBody(survey, companyDetails, psoName),
                    isHtml: true,
                    surveyId: survey.surveyId,
                    surveyType: 'survey'
                };
            } else {
                const complaint = selectedItem as Complaint;
                const psoName = sessionStorage.getItem('psoName') || 'PSO Team';
                const companyDetails = {
                    companyName: 'HBL Support',
                    imgUrl: ''
                };
                emailData = {
                    to: emailValidation.valid,
                    subject: `HBL Support - Complaint Report - ${complaint.complaintId || complaint.id}`,
                    body: formatComplaintEmailBody(complaint, companyDetails, psoName),
                    isHtml: true,
                    complaintId: complaint.complaintId || complaint.id,
                    issueType: complaint.issueType
                };
            }

            const results = await sendEmailsWithErrorHandling(emailData);

            // Prepare result message
            let resultMessage = "";
            if (results.successful.length > 0) {
                resultMessage += `Successfully sent to ${results.successful.length} recipient(s): ${results.successful.map(r => r.email).join(', ')}`;
            }

            if (results.failed.length > 0) {
                if (resultMessage) resultMessage += "\n\n";
                resultMessage += `Failed to send to ${results.failed.length} recipient(s):\n`;
                results.failed.forEach(failure => {
                    resultMessage += `• ${failure.email}: ${failure.error}\n`;
                });
            }

            showToast(resultMessage, results.failed.length === 0 ? 'success' : 'warning');

            if (results.failed.length === 0) {
                setShowEmailModal(false);
                setEmailRecipients('');
            }
        } catch (error) {
            console.error('Error sending email:', error);
            showToast('Error sending email. Please try again.', 'error');
        } finally {
            setIsSendingEmail(false);
        }
    };

    useEffect(() => {
        if (!exportMenuOpen) return;
        const onDocMouseDown = (e: MouseEvent) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
                setExportMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, [exportMenuOpen]);

    useEffect(() => {
        if (!exportProgress.active) return;
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = 'An export is still running. Leaving now may interrupt the download.';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [exportProgress.active]);

    const beginExportProgress = (format: 'excel' | 'pdf') => {
        exportCancelledRef.current = false;
        setExportProgress({
            active: true,
            format,
            message: format === 'excel' ? 'Preparing Excel workbook…' : 'Preparing PDF…',
            percent: 2,
        });
    };

    const updateExportProgress = (percent: number, message: string) => {
        setExportProgress((p) => ({ ...p, percent: Math.min(100, Math.max(0, percent)), message }));
    };

    const endExportProgress = () => {
        setExportProgress({ active: false, format: null, message: '', percent: 0 });
    };

    const getAppliedDatePeriodLabel = (): string => {
        if (surveyCreatedFrom && surveyCreatedTo) {
            return `${surveyCreatedFrom} -----> ${surveyCreatedTo}`;
        }
        if (dateFilter === 'all') return 'All';
        if (dateFilter === 'daily') return 'Today';
        if (dateFilter === 'weekly') return 'Last 7 days';
        if (dateFilter === 'monthly') return 'Last 30 days';
        return dateFilter;
    };

    const filterSummaryLine = () => {
        const parts: string[] = [];
        parts.push(`Status: ${statusFilter}`);
        if (!(surveyCreatedFrom && surveyCreatedTo) && dateFilter !== 'all') {
            parts.push(`Quick period: ${dateFilter}`);
        }
        if (canUsePsoLocationFilters) {
            const activePsoName = psoFilterOptions.find(p => p.id === psoFilter)?.name || 'All';
            const activeLocName = locationFilterOptions.find(l => l.id === locationFilter)?.name || 'All';
            parts.push(`PSO: ${activePsoName}`, `Location: ${activeLocName}`);
        }
        if (appliedSearchTerm) parts.push(`Search: "${appliedSearchTerm}"`);
        return parts.join(' · ');
    };

    const buildSurveyExcelRows = (rows: Survey[]) =>
        rows.map((survey) => ({
            'Document ID': survey.id || '',
            'Survey ID': survey.surveyId || '',
            'PSO Name': String((survey as any).createdByName || ''),
            'Location': String((survey.branch as any)?.locationName || ''),
            'Branch Code': survey.branch?.branchCode || '',
            'Branch Name': (survey.branch?.branchName || '').toString(),
            'Address': String((survey.branch as any)?.address || ''),
            'Date of Visit': survey.dateOfVisit || '',
            'Joint Visit': survey.jointVisit || '',
            'Joint Teams': survey.jointTeams?.join('; ') || '',
            'Status': normalizeDisplayStatus(survey.status),
            'Survey Remarks': (survey as any).remarks || '',
            'Custom Issues': Array.isArray((survey as any).customIssues)
                ? (survey as any).customIssues.map((x: any) => `${x.heading || ''}: ${x.description || ''}`.trim()).join(' | ')
                : '',
            'CCTV Status': (survey as any).cctv?.status || '',
            'CCTV Issues': [...(((survey as any).cctv?.issues) || []), ...(((survey as any).cctv?.otherIssues) || [])].join('; '),
            'CCTV Remarks': (survey as any).cctv?.remarks || '',
            'BAMS Status': (survey as any).bams?.status || '',
            'BAMS Issues': [...(((survey as any).bams?.issues) || []), ...(((survey as any).bams?.otherIssues) || [])].join('; '),
            'BAMS Remarks': (survey as any).bams?.remarks || '',
            'Guarding Status': (survey as any).guarding?.status || '',
            'Guarding Issues': [...(((survey as any).guarding?.issues) || []), ...(((survey as any).guarding?.otherIssues) || [])].join('; '),
            'Guarding Remarks': (survey as any).guarding?.remarks || '',
            'Other Assessments Status': (survey as any).otherAssessments?.status || '',
            'Other Assessments Other Issues': ((survey as any).otherAssessments?.otherIssues || []).join('; '),
            'Other Assessments Remarks': (survey as any).otherAssessments?.remarks || '',
        }));

    const buildComplaintExcelRows = (rows: Complaint[]) =>
        rows.map((complaint) => ({
            'Complaint ID': complaint.complaintId || complaint.id,
            'Issue Type': complaint.issueType || '',
            'Related Survey ID': complaint.surveyData?.surveyId || '',
            'PSO Name': String((complaint.surveyData as any)?.createdByName || ''),
            'Location': String((complaint.surveyData?.branch as any)?.locationName || ''),
            'Branch Code': complaint.surveyData?.branch?.branchCode || '',
            'Branch Name': (complaint.surveyData?.branch?.branchName || '').toString(),
            'Address': String((complaint.surveyData?.branch as any)?.address || ''),
            'Date of Visit': complaint.surveyData?.dateOfVisit || '',
            'Status': normalizeDisplayStatus(complaint.status),
            'Issues': complaint.issues?.join('; ') || '',
            'Remarks': (complaint as any).remarks || '',
            'Description': complaint.description || '',
            'Resolution': complaint.resolution || '',
            'Custom Issues': Array.isArray((complaint as any).customIssues)
                ? (complaint as any).customIssues.map((x: any) => `${x.heading || ''}: ${x.description || ''}`.trim()).join(' | ')
                : '',
        }));

    const applyWorksheetColumnWidths = (ws: XLSX.WorkSheet, rows: Record<string, unknown>[], maxWch = 52) => {
        if (!rows.length) return;
        const keys = Object.keys(rows[0]);
        const cols = keys.map((key) => {
            let max = key.length;
            for (const r of rows) {
                const raw = r[key];
                const s = raw != null ? String(raw) : '';
                for (const line of s.split(/\r?\n/)) {
                    max = Math.max(max, Math.min(line.length, 240));
                }
            }
            return { wch: Math.min(max + 2, maxWch) };
        });
        ws['!cols'] = cols;
    };

    const applyExcelColsForPreambleAndData = (
        ws: XLSX.WorkSheet,
        preamble: (string | number)[][],
        dataRows: Record<string, unknown>[]
    ) => {
        const keys = dataRows.length ? Object.keys(dataRows[0]) : [];
        const ncols = Math.max(2, keys.length);
        const cols: XLSX.ColInfo[] = [];
        for (let c = 0; c < ncols; c++) {
            let max = 8;
            for (const row of preamble) {
                const cell = row[c];
                if (cell != null && cell !== '') {
                    max = Math.max(max, Math.min(String(cell).length, 160));
                }
            }
            const key = keys[c];
            if (key) {
                max = Math.max(max, key.length);
                for (const r of dataRows) {
                    const raw = r[key];
                    const s = raw != null ? String(raw) : '';
                    for (const line of s.split(/\r?\n/)) {
                        max = Math.max(max, Math.min(line.length, 240));
                    }
                }
            }
            cols.push({ wch: Math.min(max + 2, 56) });
        }
        ws['!cols'] = cols;
    };

    const exportScopeLine = (total: number, pdfMode?: 'excel' | 'pdf-full' | 'pdf-truncated') => {
        const wide = isExportWideOpen({
            appliedSearchTerm,
            dateFilter,
            surveyCreatedFrom,
            surveyCreatedTo,
            statusFilter,
            psoFilter,
            locationFilter,
            canUsePsoLocationFilters,
        });
        if (wide) return 'All rows (no filters applied)';
        if (pdfMode === 'excel' || pdfMode === undefined) {
            return `${total} row(s) match your filters; this file lists all of them`;
        }
        if (pdfMode === 'pdf-truncated') {
            return `${total} row(s) match; this PDF shows the first ${PDF_FILTERED_ROW_CAP} only — use Excel for the full list`;
        }
        return `${total} row(s) match your filters; all are included below`;
    };

    const buildExcelPdfSummaryPairs = (
        format: 'excel' | 'pdf',
        tab: 'surveys' | 'complaints',
        totalMatching: number,
        sourceRows: Survey[] | Complaint[],
        pdfTruncated: boolean
    ): (string | number)[][] => {
        const scopeMode: 'excel' | 'pdf-full' | 'pdf-truncated' =
            format === 'excel' ? 'excel' : pdfTruncated ? 'pdf-truncated' : 'pdf-full';
        const pairs: (string | number)[][] = [
            ['Report type', tab === 'surveys' ? 'Branch visit surveys' : 'Complaints register'],
            ['Total rows', totalMatching],
        ];
        if (format === 'pdf' && pdfTruncated) {
            pairs.push(['Rows in this PDF', `${Math.min(totalMatching, PDF_FILTERED_ROW_CAP)} of ${totalMatching} (first page only)`]);
        }
        pairs.push(['Included', exportScopeLine(totalMatching, scopeMode)]);
        pairs.push(['Date range', getAppliedDatePeriodLabel()]);
        pairs.push(['Address', (bank?.companyAddress || '').trim() || '—']);
        pairs.push(['Filters', filterSummaryLine()]);
        if (showPsoLocationColumns) {
            pairs.push([
                'PSO',
                canUsePsoLocationFilters && psoFilter !== 'all' ? (psoFilterOptions.find(p => p.id === psoFilter)?.name || psoFilter) : 'All',
            ]);
        }
        if (tab === 'surveys') {
            pairs.push(['Status breakdown', summarizeStatusesFromSurveys(sourceRows as Survey[])]);
        } else {
            pairs.push(['Status breakdown', summarizeStatusesFromComplaints(sourceRows as Complaint[])]);
        }
        pairs.push(['Generated', new Date().toLocaleString()]);
        pairs.push(['Source', 'PSO surveys & complaints export']);
        return pairs;
    };

    const writeStyledExcelWorkbook = (
        sheetName: string,
        title: string,
        worksheetData: Record<string, unknown>[],
        fileName: string,
        summaryPairs: (string | number)[][],
        branchSheetRows?: Record<string, unknown>[]
    ) => {
        const ws: XLSX.WorkSheet = {};
        const preamble: (string | number)[][] = [[title, ''], ['Report summary', ''], ...summaryPairs, ['', '']];
        XLSX.utils.sheet_add_aoa(ws, preamble, { origin: 'A1' });
        const nextRow = preamble.length + 1;
        XLSX.utils.sheet_add_json(ws, worksheetData, { origin: `A${nextRow}`, skipHeader: false });
        applyExcelColsForPreambleAndData(ws, preamble, worksheetData);
        (ws as any)['!views'] = [{ state: 'frozen', ySplit: preamble.length, topLeftCell: `A${nextRow}`, activePane: 'bottomLeft', zoomScale: 100 }];
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, ws, sheetName.slice(0, 31));
        if (branchSheetRows && branchSheetRows.length) {
            const bws = XLSX.utils.json_to_sheet(branchSheetRows);
            (bws as any)['!cols'] = [
                { wch: 14 },
                { wch: 24 },
                { wch: 28 },
                { wch: 12 },
                { wch: 12 },
                { wch: 12 },
                { wch: 12 },
                { wch: 36 },
            ];
            XLSX.utils.book_append_sheet(workbook, bws, 'Branches');
        }
        XLSX.writeFile(workbook, fileName);
    };

    const loadAllSurveysForExport = async (): Promise<Survey[]> => {
        if (surveyTotalPages <= 1) {
            return [...filteredSurveys];
        }
        return fetchAllSurveysForExport({
            isBankUser: isBankUser || isMISIncharge,
            psoDesignation: pso?.designation,
            searchTerm: appliedSearchTerm,
            selectedPsoId: psoFilter,
            selectedLocationId: locationFilter,
            dateFilter,
            createdFromYmd: surveyCreatedFrom,
            createdToYmd: surveyCreatedTo,
            statusFilter,
            pageSize: 100,
            shouldAbort: () => exportCancelledRef.current,
            onProgress: ({ loaded, pageIndex, hasMore }) => {
                const pct = Math.min(68, 8 + pageIndex * 14 + (hasMore ? 0 : 10));
                updateExportProgress(
                    pct,
                    hasMore ? `Downloading surveys… ${loaded} row(s) so far` : `Loaded ${loaded} survey row(s)`
                );
            },
        });
    };

    const getFilteredComplaintsSnapshot = () =>
        filterComplaintsList(complaints, {
            appliedSearchTerm,
            dateFilter,
            surveyCreatedFrom,
            surveyCreatedTo,
            psoFilter,
            locationFilter,
            statusFilter,
            canUsePsoLocationFilters,
        });

    const runExportExcel = async () => {
        const tab = activeTab;
        const showHeavyUi =
            tab === 'complaints'
                ? getFilteredComplaintsSnapshot().length >= LARGE_EXPORT_MIN_ROWS
                : surveyTotalPages > 1 || filteredSurveys.length >= LARGE_EXPORT_MIN_ROWS;

        if (showHeavyUi) beginExportProgress('excel');

        try {
            let worksheetData: Record<string, unknown>[] = [];
            let sheetName = 'Export';
            let sourceRowsForSummary: Survey[] | Complaint[] = [];
            if (tab === 'surveys') {
                if (showHeavyUi) updateExportProgress(6, 'Collecting survey rows…');
                const rows = await loadAllSurveysForExport();
                if (exportCancelledRef.current) {
                    showToast('Export cancelled', 'warning');
                    return;
                }
                if (!rows.length) {
                    showToast('No surveys match the current filters', 'warning');
                    return;
                }
                sourceRowsForSummary = rows;
                await new Promise<void>((r) => requestAnimationFrame(() => r()));
                if (showHeavyUi) updateExportProgress(78, 'Building Excel…');
                worksheetData = buildSurveyExcelRows(rows);
                sheetName = 'Surveys';
            } else {
                const rows = getFilteredComplaintsSnapshot();
                if (!rows.length) {
                    showToast('No complaints match the current filters', 'warning');
                    return;
                }
                sourceRowsForSummary = rows;
                if (showHeavyUi) updateExportProgress(25, 'Building Excel…');
                worksheetData = buildComplaintExcelRows(rows);
                sheetName = 'Complaints';
            }

            if (showHeavyUi) updateExportProgress(92, 'Writing file…');
            await new Promise<void>((r) => requestAnimationFrame(() => r()));
            const fileName = `${tab === 'surveys' ? 'Survey' : 'Complaint'}_Full_${dateFilter}_${new Date().toISOString().split('T')[0]}.xlsx`;
            const summaryPairs = buildExcelPdfSummaryPairs('excel', tab, sourceRowsForSummary.length, sourceRowsForSummary, false);
            const branchSheetRows: Record<string, unknown>[] =
                tab === 'surveys'
                    ? buildBranchMetricsFromSurveys(sourceRowsForSummary as Survey[]).map((m) => ({
                          'Branch code': m.branchCode,
                          'Location / branch': m.locationOrBranch,
                          Address: m.address,
                          'Total visits': m.totalVisits,
                          Pending: m.pendingVisits,
                          Resolved: m.resolvedVisits,
                          'Joint visit': m.jointVisitYes,
                          'Majority issue': m.majorityIssue,
                      }))
                    : buildBranchMetricsFromComplaints(sourceRowsForSummary as Complaint[]).map((m) => ({
                          'Branch code': m.branchCode,
                          'Location / branch': m.locationOrBranch,
                          Address: m.address,
                          'Total visits': m.totalVisits,
                          Pending: m.pendingVisits,
                          Resolved: m.resolvedVisits,
                          'Joint visit': m.jointVisitYes,
                          'Majority issue': m.majorityIssue,
                      }));
            writeStyledExcelWorkbook(
                sheetName,
                tab === 'surveys' ? 'Survey export (full data)' : 'Complaint export (full data)',
                worksheetData,
                fileName,
                summaryPairs,
                branchSheetRows
            );
            if (showHeavyUi) updateExportProgress(100, 'Download complete');
            showToast('Excel exported successfully', 'success');
        } catch (err) {
            console.error(err);
            showToast('Export failed. Please try again.', 'error');
        } finally {
            setTimeout(() => endExportProgress(), 600);
        }
    };

    const runExportPdfSummary = async () => {
        const tab = activeTab;
        const showHeavyUi =
            tab === 'complaints'
                ? getFilteredComplaintsSnapshot().length >= LARGE_EXPORT_MIN_ROWS
                : surveyTotalPages > 1 || filteredSurveys.length >= LARGE_EXPORT_MIN_ROWS;

        if (showHeavyUi) beginExportProgress('pdf');

        try {
            if (showHeavyUi) updateExportProgress(8, 'Collecting data…');
            let rows: (Survey | Complaint)[] = [];
            let total = 0;
            if (tab === 'surveys') {
                rows = await loadAllSurveysForExport();
                if (exportCancelledRef.current) {
                    showToast('Export cancelled', 'warning');
                    return;
                }
                total = rows.length;
            } else {
                rows = getFilteredComplaintsSnapshot();
                total = rows.length;
            }

            if (!total) {
                showToast(`No ${tab} to export`, 'warning');
                return;
            }

            const wideOpen = isExportWideOpen({
                appliedSearchTerm,
                dateFilter,
                surveyCreatedFrom,
                surveyCreatedTo,
                statusFilter,
                psoFilter,
                locationFilter,
                canUsePsoLocationFilters,
            });
            const pdfTruncated = !wideOpen && total > PDF_FILTERED_ROW_CAP;
            const tableRows = pdfTruncated ? rows.slice(0, PDF_FILTERED_ROW_CAP) : rows;

            if (showHeavyUi) updateExportProgress(55, 'Rendering PDF…');
            await new Promise<void>((r) => requestAnimationFrame(() => r()));

            let bankLogo: { dataUrl: string; format: 'PNG'; w: number; h: number } | null = null;
            const logoUrl = typeof bank?.imgUrl === 'string' ? bank.imgUrl.trim() : '';
            if (logoUrl) {
                try {
                    bankLogo = await tryLoadImageForPdf(logoUrl);
                } catch {
                    bankLogo = null;
                }
            }

            const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
            const pageW = doc.internal.pageSize.getWidth();

            doc.setFillColor(41, 128, 185);
            doc.rect(0, 0, pageW, 26, 'F');

            let titleX = 12;
            const subtitle = tab === 'surveys' ? 'Visit surveys · PDF export' : 'Complaints register · PDF export';
            const mainTitle = tab === 'surveys' ? 'Branch Survey Report' : 'Branch Complaints Report';

            if (bankLogo?.dataUrl && bankLogo.w > 0 && bankLogo.h > 0) {
                try {
                    const maxW = 26;
                    const maxH = 17;
                    let drawW = maxW;
                    let drawH = (bankLogo.h / bankLogo.w) * drawW;
                    if (drawH > maxH) {
                        drawH = maxH;
                        drawW = (bankLogo.w / bankLogo.h) * drawH;
                    }
                    doc.addImage(bankLogo.dataUrl, 'PNG', 10, 5.5, drawW, drawH);
                    titleX = 10 + drawW + 4;
                } catch {
                    titleX = 12;
                }
            }

            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(14);
            doc.text(mainTitle, titleX, 11);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.text(subtitle, titleX, 18);

            const summaryBody = buildExcelPdfSummaryPairs('pdf', tab, total, rows, pdfTruncated).map((pair) => [
                String(pair[0]),
                String(pair[1]),
            ]);

            autoTable(doc, {
                startY: 22,
                head: [['Summary', 'Details']],
                body: summaryBody,
                theme: 'striped',
                styles: { fontSize: 8.5, cellPadding: 1.5, lineColor: [220, 224, 226], lineWidth: 0.1, textColor: 44 },
                headStyles: { fillColor: [52, 73, 94], textColor: 255, fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [252, 252, 253] },
                columnStyles: { 0: { cellWidth: 52, fontStyle: 'bold' }, 1: { cellWidth: pageW - 12 * 2 - 52 } },
                margin: { left: 12, right: 12 },
            });

            const branchMetricsPdf =
                tab === 'surveys'
                    ? buildBranchMetricsFromSurveys(rows as Survey[])
                    : buildBranchMetricsFromComplaints(rows as Complaint[]);

            doc.addPage();
            let sectionTop = 14;

            if (branchMetricsPdf.length) {
                doc.setFontSize(9);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(44, 62, 80);
                doc.text('Branches', 12, sectionTop);
                autoTable(doc, {
                    startY: sectionTop + 3,
                    head: [['Branch', 'Location / branch', 'Address', 'Visits', 'Pending', 'Resolved', 'Joint visit', 'Majority issue']],
                    body: branchMetricsPdf.map((m) => [
                        m.branchCode,
                        m.locationOrBranch,
                        m.address,
                        String(m.totalVisits),
                        String(m.pendingVisits),
                        String(m.resolvedVisits),
                        String(m.jointVisitYes),
                        m.majorityIssue,
                    ]),
                    theme: 'grid',
                    styles: { fontSize: 7.5, cellPadding: 1.2, lineColor: [200, 200, 200], lineWidth: 0.1 },
                    headStyles: { fillColor: [52, 73, 94], textColor: 255, fontStyle: 'bold' },
                    alternateRowStyles: { fillColor: [252, 252, 253] },
                    margin: { left: 12, right: 12 },
                });
                doc.addPage();
                sectionTop = 14;
            }

            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(44, 62, 80);
            doc.text('Detail table', 12, sectionTop);

            const head =
                tab === 'surveys'
                    ? [['Survey ID', 'Branch', 'Location', 'Address', 'PSO', 'Visit date', 'Status']]
                    : [['Complaint ID', 'Issue', 'Survey ID', 'Branch', 'Location', 'Address', 'PSO', 'Status']];

            const body =
                tab === 'surveys'
                    ? (tableRows as Survey[]).map((s) => [
                          s.surveyId || '—',
                          s.branch?.branchCode || '—',
                          String((s.branch as any)?.locationName || '—'),
                          branchAddressOnly(s.branch),
                          String((s as any).createdByName || '—'),
                          s.dateOfVisit || '—',
                          normalizeDisplayStatus(s.status),
                      ])
                    : (tableRows as Complaint[]).map((c) => [
                          String(c.complaintId || c.id),
                          c.issueType || '—',
                          c.surveyData?.surveyId || '—',
                          c.surveyData?.branch?.branchCode || '—',
                          String((c.surveyData?.branch as any)?.locationName || '—'),
                          branchAddressOnly(c.surveyData?.branch),
                          String((c.surveyData as any)?.createdByName || '—'),
                          normalizeDisplayStatus(c.status),
                      ]);

            autoTable(doc, {
                startY: sectionTop + 3,
                head,
                body,
                styles: { fontSize: 8, cellPadding: 1.4, lineColor: [220, 220, 220], lineWidth: 0.1 },
                headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [248, 249, 250] },
                margin: { left: 12, right: 12 },
            });

            const finalY = ((doc as any).lastAutoTable?.finalY as number) || sectionTop + 30;
            const pageWLast = doc.internal.pageSize.getWidth();
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            if (pdfTruncated) {
                doc.setTextColor(180, 83, 9);
                doc.text(
                    `Note: Only the first ${PDF_FILTERED_ROW_CAP} rows are shown. Export to Excel for all ${total} rows.`,
                    12,
                    Math.min(finalY + 10, 200),
                    { maxWidth: pageWLast - 24 }
                );
            } else if (!wideOpen) {
                doc.setTextColor(90, 90, 90);
                doc.text(
                    'All rows that match your filters are included in the table above.',
                    12,
                    Math.min(finalY + 10, 200),
                    { maxWidth: pageWLast - 24 }
                );
            } else {
                doc.setTextColor(90, 90, 90);
                doc.text(
                    'No filters were applied. Every row is included in the table above.',
                    12,
                    Math.min(finalY + 10, 200),
                    { maxWidth: pageWLast - 24 }
                );
            }

            const fileName = `${tab === 'surveys' ? 'Survey' : 'Complaint'}_Report_${dateFilter}_${new Date().toISOString().split('T')[0]}.pdf`;
            doc.save(fileName);
            if (showHeavyUi) updateExportProgress(100, 'Download complete');
            showToast('PDF exported successfully', 'success');
        } catch (err) {
            console.error(err);
            showToast('PDF export failed. Please try again.', 'error');
        } finally {
            setTimeout(() => endExportProgress(), 600);
        }
    };

    const handleExportChoice = (format: 'excel' | 'pdf') => {
        setExportMenuOpen(false);
        if (exportProgress.active) {
            showToast('Another export is already running', 'warning');
            return;
        }
        void (format === 'excel' ? runExportExcel() : runExportPdfSummary());
    };

    const cancelOngoingExport = () => {
        exportCancelledRef.current = true;
        setExportProgress((p) => (p.active ? { ...p, message: 'Cancelling after current step…' } : p));
    };

    const downloadItemPDF = (item: Survey | Complaint) => {
        const doc = new jsPDF();
        let currentY = 15;

        // Banner header (HBL Green)
        doc.setFillColor(0, 130, 105);
        doc.rect(0, 0, 210, 40, 'F');

        doc.setFontSize(22);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text('HBL BRANCH AUDIT & SUPPORT REPORT', 14, 25);

        // Date under the banner
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(240, 240, 240);
        const currentDate = new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
        doc.text(`Generated: ${currentDate}`, 14, 33);

        currentY = 50;

        if (activeTab === 'surveys') {
            const survey = item as Survey;
            
            // Survey Details Header
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(14);
            doc.setTextColor(33, 37, 41);
            doc.text('SURVEY INFORMATION', 14, currentY);
            
            // Add a clean underline bar below section header
            doc.setDrawColor(0, 130, 105);
            doc.setLineWidth(1);
            doc.line(14, currentY + 2, 196, currentY + 2);
            currentY += 10;

            const surveyDetails = [
                ['Survey ID', survey.surveyId || 'N/A'],
                ['Branch Code', survey.branch?.branchCode || 'N/A'],
                ['Branch Address', survey.branch?.address || 'N/A'],
                ['Date of Visit', survey.dateOfVisit || 'N/A'],
                ['Joint Visit', survey.jointVisit || 'N/A'],
                ['Joint Teams', survey.jointTeams?.join(', ') || 'None'],
                ['Survey Status', survey.status || 'Pending'],
            ];

            autoTable(doc, {
                startY: currentY,
                head: [['Field', 'Detail Information']],
                body: surveyDetails,
                styles: { fontSize: 10, cellPadding: 3.5, font: 'helvetica' },
                headStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: 'bold' },
                columnStyles: {
                    0: { fontStyle: 'bold', cellWidth: 45 },
                    1: { cellWidth: 135 }
                },
                theme: 'striped'
            });

            currentY = (doc as any).lastAutoTable.finalY + 15;

            // Fetch complaints of this survey
            const surveyComplaints = complaints.filter(c => c.surveyId === survey.id);
            
            if (surveyComplaints.length > 0) {
                // Check if page overflow
                if (currentY > 240) {
                    doc.addPage();
                    currentY = 20;
                }

                doc.setFont('helvetica', 'bold');
                doc.setFontSize(14);
                doc.setTextColor(33, 37, 41);
                doc.text('COMPLAINTS & ISSUES DETAIL', 14, currentY);
                
                doc.setDrawColor(0, 130, 105);
                doc.setLineWidth(1);
                doc.line(14, currentY + 2, 196, currentY + 2);
                currentY += 10;

                const getHeaderType = (issueType?: string) => {
                    const lower = (issueType || '').toLowerCase();
                    if (lower.includes('cctv')) return 'CCTV';
                    if (lower.includes('bams')) return 'BAMS';
                    if (lower.includes('guard') || lower.includes('security')) return 'GUARDS';
                    if (lower.includes('other')) return 'OTHER';
                    return (issueType || 'GENERAL').toUpperCase();
                };

                surveyComplaints.forEach((comp, compIdx) => {
                    // Check if space left is enough, otherwise add page
                    if (currentY > 230) {
                        doc.addPage();
                        currentY = 20;
                    }

                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(11);
                    doc.setTextColor(0, 130, 105);
                    doc.text(`${compIdx + 1}. Assessment Type: ${getHeaderType(comp.issueType)} (ID: ${comp.complaintId || comp.id})`, 14, currentY);
                    
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(10);
                    doc.setTextColor(80, 80, 80);
                    doc.text(`Status: ${comp.status || 'Pending'}`, 140, currentY);
                    
                    currentY += 6;

                    // Build issues checklist layout
                    if (comp.issues && comp.issues.length > 0) {
                        const issuesRows = comp.issues.map((issue) => {
                            const isResolved = comp.resolvedIssues?.includes(issue) || false;
                            const checkmark = isResolved ? '[x] RESOLVED' : '[ ] PENDING';
                            return [checkmark, issue];
                        });

                        autoTable(doc, {
                            startY: currentY,
                            head: [['Status Check', 'Issue / Deficiency Detail']],
                            body: issuesRows,
                            styles: { fontSize: 9, cellPadding: 2, font: 'helvetica' },
                            headStyles: { fillColor: [127, 140, 141], textColor: 255, fontStyle: 'bold' },
                            columnStyles: {
                                0: { fontStyle: 'bold', cellWidth: 35 },
                                1: { cellWidth: 145 }
                            },
                            theme: 'plain'
                        });

                        currentY = (doc as any).lastAutoTable.finalY + 8;
                    } else {
                        doc.setFont('helvetica', 'italic');
                        doc.setFontSize(9.5);
                        doc.setTextColor(120, 120, 120);
                        doc.text('No specific checklist items.', 18, currentY);
                        currentY += 8;
                    }

                    if (comp.description) {
                        doc.setFont('helvetica', 'normal');
                        doc.setFontSize(9);
                        doc.setTextColor(100, 100, 100);
                        const descLines = doc.splitTextToSize(`Description: ${comp.description}`, 170);
                        doc.text(descLines, 18, currentY);
                        currentY += descLines.length * 5 + 4;
                    }
                    currentY += 4; // Spacing between complaints
                });
            } else {
                doc.setFont('helvetica', 'italic');
                doc.setFontSize(11);
                doc.setTextColor(120, 120, 120);
                doc.text('No linked complaints or issues recorded for this survey.', 14, currentY);
                currentY += 15;
            }
        } else {
            // Complaint Details PDF Report
            const complaint = item as Complaint;

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(14);
            doc.setTextColor(33, 37, 41);
            doc.text('COMPLAINT INFORMATION', 14, currentY);
            
            doc.setDrawColor(0, 130, 105);
            doc.setLineWidth(1);
            doc.line(14, currentY + 2, 196, currentY + 2);
            currentY += 10;

            const complaintDetails = [
                ['Complaint ID', complaint.complaintId || complaint.id],
                ['Issue Type', complaint.issueType || 'N/A'],
                ['Survey ID', complaint.surveyData?.surveyId || 'N/A'],
                ['Branch Code', complaint.surveyData?.branch?.branchCode || 'N/A'],
                ['Branch Address', complaint.surveyData?.branch?.address || 'N/A'],
                ['Date of Visit', complaint.surveyData?.dateOfVisit || 'N/A'],
                ['Complaint Status', complaint.status || 'Pending'],
            ];

            autoTable(doc, {
                startY: currentY,
                head: [['Field', 'Detail Information']],
                body: complaintDetails,
                styles: { fontSize: 10, cellPadding: 3.5, font: 'helvetica' },
                headStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: 'bold' },
                columnStyles: {
                    0: { fontStyle: 'bold', cellWidth: 45 },
                    1: { cellWidth: 135 }
                },
                theme: 'striped'
            });

            currentY = (doc as any).lastAutoTable.finalY + 15;

            // Issues List
            if (complaint.issues && complaint.issues.length > 0) {
                if (currentY > 230) {
                    doc.addPage();
                    currentY = 20;
                }

                doc.setFont('helvetica', 'bold');
                doc.setFontSize(14);
                doc.setTextColor(33, 37, 41);
                doc.text('ISSUES & DEFICIENCIES CHECKLIST', 14, currentY);
                
                doc.setDrawColor(0, 130, 105);
                doc.setLineWidth(1);
                doc.line(14, currentY + 2, 196, currentY + 2);
                currentY += 10;

                const issuesRows = complaint.issues.map((issue) => {
                    const isResolved = complaint.resolvedIssues?.includes(issue) || false;
                    const checkmark = isResolved ? '[x] RESOLVED' : '[ ] PENDING';
                    return [checkmark, issue];
                });

                autoTable(doc, {
                    startY: currentY,
                    head: [['Status Check', 'Deficiency Detail']],
                    body: issuesRows,
                    styles: { fontSize: 10, cellPadding: 3, font: 'helvetica' },
                    headStyles: { fillColor: [127, 140, 141], textColor: 255, fontStyle: 'bold' },
                    columnStyles: {
                        0: { fontStyle: 'bold', cellWidth: 45 },
                        1: { cellWidth: 135 }
                    },
                    theme: 'plain'
                });

                currentY = (doc as any).lastAutoTable.finalY + 15;
            }

            if (complaint.description) {
                if (currentY > 230) {
                    doc.addPage();
                    currentY = 20;
                }

                doc.setFont('helvetica', 'bold');
                doc.setFontSize(12);
                doc.setTextColor(33, 37, 41);
                doc.text('DESCRIPTION', 14, currentY);
                currentY += 8;

                doc.setFont('helvetica', 'normal');
                doc.setFontSize(10);
                doc.setTextColor(60, 60, 60);
                const descLines = doc.splitTextToSize(complaint.description, 180);
                doc.text(descLines, 14, currentY);
                currentY += descLines.length * 6 + 10;
            }

            if (complaint.resolution) {
                if (currentY > 230) {
                    doc.addPage();
                    currentY = 20;
                }

                doc.setFont('helvetica', 'bold');
                doc.setFontSize(12);
                doc.setTextColor(33, 37, 41);
                doc.text('RESOLUTION', 14, currentY);
                currentY += 8;

                doc.setFont('helvetica', 'normal');
                doc.setFontSize(10);
                doc.setTextColor(60, 60, 60);
                const resLines = doc.splitTextToSize(complaint.resolution, 180);
                doc.text(resLines, 14, currentY);
                currentY += resLines.length * 6 + 10;
            }
        }

        // Signature section
        if (currentY > 220) {
            doc.addPage();
            currentY = 30;
        } else {
            currentY += 15;
        }

        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(44, 62, 80);
        doc.text('Signatures & Acknowledgements:', 14, currentY);

        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.5);
        doc.line(14, currentY + 4, 196, currentY + 4);
        
        currentY += 15;

        // Signature lines side by side
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(80, 80, 80);

        doc.text('Prepared By: _____________________', 14, currentY);
        doc.text('Approved By: _____________________', 110, currentY);

        doc.text('Designation: _____________________', 14, currentY + 10);
        doc.text('Designation: _____________________', 110, currentY + 10);

        doc.text('Date: ___________________________', 14, currentY + 20);
        doc.text('Date: ___________________________', 110, currentY + 20);

        const fileName = `${activeTab === 'surveys' ? 'Survey' : 'Complaint'}_${activeTab === 'surveys' ? (item as Survey).surveyId : (item as Complaint).complaintId || (item as Complaint).id}_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);
        showToast('PDF downloaded successfully', 'success');
    };
    return (
        <>
            <div className="p-6">
                {/* Toast */}
                {toast.show && (
                    <div className="pointer-events-none fixed right-4 top-4 z-50">
                        <div className="pointer-events-auto">
                            <Toast
                                message={toast.message}
                                type={toast.type}
                                onClose={() => setToast({ ...toast, show: false })}
                            />
                        </div>
                    </div>
                )}

                {rowContextMenu && (
                    <div
                        id="surveys-table-row-context"
                        role="menu"
                        className="fixed z-[200] min-w-[11rem] overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-xl dark:border-gray-600 dark:bg-gray-900"
                        style={{
                            left: Math.min(rowContextMenu.clientX, (typeof window !== 'undefined' ? window.innerWidth : 1200) - 180),
                            top: Math.min(rowContextMenu.clientY, (typeof window !== 'undefined' ? window.innerHeight : 800) - 8),
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        {rowContextMenu.kind === 'survey' ? (
                            <>
                                <button
                                    type="button"
                                    role="menuitem"
                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-800"
                                    onClick={() => {
                                        openDetailsModal(rowContextMenu.survey);
                                        setRowContextMenu(null);
                                    }}
                                >
                                    <Icon icon={FiEye} className="h-4 w-4 shrink-0 opacity-70" />
                                    View
                                </button>
                                {!isHead && (
                                    <button
                                        type="button"
                                        role="menuitem"
                                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-800"
                                        onClick={() => {
                                            openEmailModal(rowContextMenu.survey);
                                            setRowContextMenu(null);
                                        }}
                                    >
                                        <Icon icon={FiMail} className="h-4 w-4 shrink-0 opacity-70" />
                                        Email
                                    </button>
                                )}
                                <button
                                    type="button"
                                    role="menuitem"
                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-800"
                                    onClick={() => {
                                        downloadItemPDF(rowContextMenu.survey);
                                        setRowContextMenu(null);
                                    }}
                                >
                                    <Icon icon={FiDownload} className="h-4 w-4 shrink-0 opacity-70" />
                                    PDF
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    role="menuitem"
                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-800"
                                    onClick={() => {
                                        openDetailsModal(rowContextMenu.complaint);
                                        setRowContextMenu(null);
                                    }}
                                >
                                    <Icon icon={FiEye} className="h-4 w-4 shrink-0 opacity-70" />
                                    View
                                </button>
                                <button
                                    type="button"
                                    role="menuitem"
                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-800"
                                    onClick={() => {
                                        openEmailModal(rowContextMenu.complaint);
                                        setRowContextMenu(null);
                                    }}
                                >
                                    <Icon icon={FiMail} className="h-4 w-4 shrink-0 opacity-70" />
                                    Email
                                </button>
                                <button
                                    type="button"
                                    role="menuitem"
                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-800"
                                    onClick={() => {
                                        downloadItemPDF(rowContextMenu.complaint);
                                        setRowContextMenu(null);
                                    }}
                                >
                                    <Icon icon={FiDownload} className="h-4 w-4 shrink-0 opacity-70" />
                                    PDF
                                </button>
                            </>
                        )}
                    </div>
                )}

                {/* Header */}
                <div className="mb-6 flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Surveys & Complaints</h2>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">Manage and track survey operations and complaints</p>
                    </div>
                    {activeTab === 'surveys' && !isBankUser && !isMISIncharge && !isHead && (
                        <Button
                            onClick={() => setShowAddModal(true)}
                            className="flex items-center gap-2"
                        >
                            <Icon icon={FiPlus} className="h-4 w-4" />
                            Add Survey
                        </Button>
                    )}
                </div>

                {/* Tabs */}
                <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveTab('surveys')}
                            className={`whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium ${activeTab === 'surveys'
                                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                }`}
                        >
                            Surveys
                        </button>
                        <button
                            onClick={() => setActiveTab('complaints')}
                            className={`whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium ${activeTab === 'complaints'
                                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                }`}
                        >
                            Complaints
                        </button>
                    </nav>
                </div>

                {/* Search and Filter Controls */}
                <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex w-full flex-1 items-stretch gap-3 md:pr-3">
                        <div className="min-w-0 flex-1">
                            <TextField
                                type="text"
                                className="!h-11"
                                placeholder={activeTab === 'surveys' ? 'Search by Survey ID, Branch Code, or Branch Name...' : 'Search by Complaint ID, Issue Type, PSO, Location, or Status...'}
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        applySearch();
                                    }
                                }}
                                prefix={<Icon icon={FiSearch} />}
                                suffix={
                                    searchInput ? (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSearchInput('');
                                                setAppliedSearchTerm('');
                                                setCurrentPage(1);
                                            }}
                                            className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                                            aria-label="Clear search"
                                            title="Clear search"
                                        >
                                            <Icon icon={FiX} className="h-4 w-4" />
                                        </button>
                                    ) : null
                                }
                            />
                        </div>
                        {activeTab === 'surveys' && (
                            <Button onClick={applySearch} className="!h-11 !py-0 flex items-center gap-2">
                                <Icon icon={FiSearch} className="h-4 w-4" />
                                Search
                            </Button>
                        )}
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-3">
                        <div className="relative" ref={exportMenuRef}>
                            <button
                                type="button"
                                onClick={() => setExportMenuOpen((o) => !o)}
                                disabled={exportProgress.active}
                                className="inline-flex h-11 items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                            >
                                <Icon icon={FiDownload} className="h-4 w-4" />
                                Export
                                <Icon icon={FiChevronDown} className={`h-4 w-4 shrink-0 transition-transform ${exportMenuOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {exportMenuOpen && !exportProgress.active && (
                                <div className="absolute right-0 z-40 mt-1 w-52 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-xl ring-1 ring-black/5 dark:border-gray-600 dark:bg-gray-900">
                                    <button
                                        type="button"
                                        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-gray-800 hover:bg-emerald-50 dark:text-gray-100 dark:hover:bg-emerald-900/20"
                                        onClick={() => handleExportChoice('excel')}
                                    >
                                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-600 text-xs font-bold text-white">XLS</span>
                                        <span>
                                            <span className="block">Excel workbook</span>
                                            <span className="text-xs font-normal text-gray-500 dark:text-gray-400">Full filtered columns</span>
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-gray-800 hover:bg-rose-50 dark:text-gray-100 dark:hover:bg-rose-900/20"
                                        onClick={() => handleExportChoice('pdf')}
                                    >
                                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-600 text-xs font-bold text-white">PDF</span>
                                        <span>
                                            <span className="block">PDF report</span>
                                            <span className="text-xs font-normal text-gray-500 dark:text-gray-400">Full list if unfiltered; capped when filtered</span>
                                        </span>
                                    </button>
                                </div>
                            )}
                        </div>
                        <Button variant="secondary" onClick={openFilterModal} className="flex items-center gap-2">
                            <Icon icon={FiFileText} className="h-4 w-4" />
                            Filters
                        </Button>
                        {hasActiveSurveyFilters && (
                            <Button variant="secondary" onClick={clearFilters}>
                                Clear Filters
                            </Button>
                        )}
                    </div>
                </div>

                {/* Surveys Table */}
                {activeTab === 'surveys' && (
                    <Table
                        data={paginatedSurveys}
                        columns={surveyColumns}
                        keyExtractor={(item) => item.id}
                        loading={loading}
                        emptyMessage={appliedSearchTerm || psoFilter !== 'all' || locationFilter !== 'all' || dateFilter !== 'all' || !!(surveyCreatedFrom && surveyCreatedTo) || statusFilter !== 'all' ? 'Try adjusting your search or filter' : 'Get started by creating your first survey'}
                        onRowClick={(survey) => openDetailsModal(survey)}
                        onRowContextMenu={(e, survey) => {
                            e.preventDefault();
                            setRowContextMenu({ kind: 'survey', survey, clientX: e.clientX, clientY: e.clientY });
                        }}
                        pagination={{
                            currentPage,
                            totalPages: surveyTotalPages,
                            onPageChange: (page) => {
                                void loadSurveyPage(page);
                            }
                        }}
                    />
                )}

                {/* Complaints Table */}
                {activeTab === 'complaints' && (
                    <Table
                        data={paginatedComplaints}
                        columns={complaintColumns}
                        keyExtractor={(item) => item.id}
                        loading={loading}
                        emptyMessage={appliedSearchTerm || dateFilter !== 'all' || !!(surveyCreatedFrom && surveyCreatedTo) || statusFilter !== 'all' || (canUsePsoLocationFilters && (psoFilter !== 'all' || locationFilter !== 'all')) ? 'Try adjusting your search or filter' : 'No complaints have been registered yet'}
                        onRowClick={(complaint) => openDetailsModal(complaint)}
                        onRowContextMenu={(e, complaint) => {
                            e.preventDefault();
                            setRowContextMenu({ kind: 'complaint', complaint, clientX: e.clientX, clientY: e.clientY });
                        }}
                        pagination={{
                            currentPage,
                            totalPages: Math.ceil(filteredComplaints.length / perPage),
                            onPageChange: setCurrentPage
                        }}
                    />
                )}
            </div>

            <Modal
                isOpen={showFilterModal}
                onClose={() => setShowFilterModal(false)}
                title={activeTab === 'surveys' ? 'Filter surveys' : 'Filter complaints'}
                size="lg"
            >
                <div className="space-y-4 p-4">
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Quick period</label>
                        <select
                            value={draftDateFilter}
                            onChange={(e) => {
                                const v = e.target.value as 'all' | 'daily' | 'weekly' | 'monthly';
                                setDraftDateFilter(v);
                                setDraftSurveyCreatedFrom(null);
                                setDraftSurveyCreatedTo(null);
                            }}
                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                        >
                            <option value="all">All Time</option>
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                        </select>
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Date range</label>
                        <DatePicker.RangePicker
                            className="w-full [&_.ant-picker]:min-h-[48px] [&_.ant-picker]:w-full [&_.ant-picker-input>input]:text-sm"
                            value={
                                draftSurveyCreatedFrom && draftSurveyCreatedTo
                                    ? [dayjs(draftSurveyCreatedFrom), dayjs(draftSurveyCreatedTo)]
                                    : null
                            }
                            allowClear
                            format="MMM D, YYYY"
                            onChange={(dates) => {
                                if (!dates || !dates[0] || !dates[1]) {
                                    setDraftSurveyCreatedFrom(null);
                                    setDraftSurveyCreatedTo(null);
                                    return;
                                }
                                setDraftSurveyCreatedFrom(dates[0].format('YYYY-MM-DD'));
                                setDraftSurveyCreatedTo(dates[1].format('YYYY-MM-DD'));
                                setDraftDateFilter('all');
                            }}
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Status</label>
                        <select
                            value={draftStatusFilter}
                            onChange={(e) => setDraftStatusFilter(e.target.value as SurveyStatusFilter)}
                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                        >
                            <option value="all">All Statuses</option>
                            <option value="pending">Pending / No Status</option>
                            <option value="resolved">Resolved</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                        </select>
                    </div>
                    {canUsePsoLocationFilters && (
                        <>
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">PSO</label>
                                <select
                                    value={draftPsoFilter}
                                    onChange={(e) => setDraftPsoFilter(e.target.value)}
                                    className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                                >
                                    <option value="all">All PSO</option>
                                    {psoFilterOptions.map((item) => (
                                        <option key={item.id} value={item.id}>{item.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Location</label>
                                <select
                                    value={draftLocationFilter}
                                    onChange={(e) => setDraftLocationFilter(e.target.value)}
                                    className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                                >
                                    <option value="all">All Location</option>
                                    {locationFilterOptions.map((item) => (
                                        <option key={item.id} value={item.id}>{item.name}</option>
                                    ))}
                                </select>
                            </div>
                        </>
                    )}
                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="secondary" onClick={() => setShowFilterModal(false)}>Cancel</Button>
                        <Button onClick={applyFilters}>Apply</Button>
                    </div>
                </div>
            </Modal>

            {/* Details Modal */}
            {showDetailsModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
                        <div className="sticky -mt-6 -mx-6 top-0 z-20 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4 mb-6 dark:border-gray-700 dark:bg-gray-800">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                {activeTab === 'surveys' ? 'Survey Details' : 'Complaint Details'}
                            </h3>
                            <div className="flex items-center gap-2">
                                {activeTab === 'surveys' && selectedSurvey && canEditRowStatus && !isBankUser && (
                                    <>
                                        {!isEditing ? (
                                            <Button
                                                variant="secondary"
                                                className="!px-3 !py-1.5 text-xs"
                                                onClick={handleEdit}
                                            >
                                                <Icon icon={FiEdit2} className="mr-1 h-3 w-3" />
                                                Edit
                                            </Button>
                                        ) : (
                                            <>
                                                <Button
                                                    variant="secondary"
                                                    className="!px-3 !py-1.5 text-xs"
                                                    onClick={handleCancelEdit}
                                                    disabled={updating}
                                                >
                                                    <Icon icon={FiX} className="mr-1 h-3 w-3" />
                                                    Cancel
                                                </Button>
                                                <Button
                                                    className="!px-3 !py-1.5 text-xs"
                                                    onClick={handleSaveEdit}
                                                    disabled={updating}
                                                >
                                                    <Icon icon={FiSave} className="mr-1 h-3 w-3" />
                                                    {updating ? 'Saving...' : 'Save'}
                                                </Button>
                                            </>
                                        )}
                                    </>
                                )}
                                
                                {/* Download PDF Button */}
                                {(() => {
                                    const item = activeTab === 'surveys' ? selectedSurvey : selectedComplaint;
                                    if (!item) return null;
                                    return (
                                        <button
                                            onClick={() => downloadItemPDF(item)}
                                            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300 transition-colors"
                                            title="Download PDF"
                                        >
                                            <Icon icon={FiDownload} className="h-5 w-5" />
                                        </button>
                                    );
                                })()}

                                {/* Close Button */}
                                <button
                                    onClick={() => {
                                        setShowDetailsModal(false);
                                        setSelectedSurvey(null);
                                        setSelectedComplaint(null);
                                        setIsEditing(false);
                                        setEditedSurvey(null);
                                    }}
                                    className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300 transition-colors"
                                    title="Close"
                                >
                                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        <div className="space-y-4">
                            {activeTab === 'surveys' && selectedSurvey && (
                                <>
                                    {(() => {
                                        const roInput =
                                            'mt-1 w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 outline-none shadow-sm dark:border-gray-600 dark:bg-black/30 dark:text-gray-100';
                                        const chip =
                                            'rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-700 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-200';
                                        const toList = (v: any): string[] => {
                                            if (!v) return [];
                                            if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
                                            if (typeof v === 'string') {
                                                return v
                                                    .split(/[;,]+/)
                                                    .map((x) => x.trim())
                                                    .filter(Boolean);
                                            }
                                            return [];
                                        };
                                        const wrap = (items?: any) =>
                                            toList(items).filter(Boolean).map((t, idx) => (
                                                <span key={`${t}-${idx}`} className={chip}>
                                                    {String(t)}
                                                </span>
                                            ));

                                        return (
                                            <>
                                                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900/30">
                                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                                        <div>
                                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Survey ID</label>
                                                            <input readOnly value={selectedSurvey.surveyId || ''} className={roInput} />
                                                        </div>
                                                        <div>
                                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Date of Visit</label>
                                                            {isEditing ? (
                                                                <input
                                                                    type="date"
                                                                    value={editedSurvey?.dateOfVisit || ''}
                                                                    onChange={(e) => setEditedSurvey({ ...editedSurvey, dateOfVisit: e.target.value })}
                                                                    className="mt-1 w-full rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm text-gray-900 outline-none shadow-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                                                                />
                                                            ) : (
                                                                <input readOnly value={selectedSurvey.dateOfVisit || ''} className={roInput} />
                                                            )}
                                                        </div>
                                                        <div>
                                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Code</label>
                                                            <input readOnly value={selectedSurvey.branch?.branchCode || ''} className={roInput} />
                                                        </div>
                                                        <div>
                                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Address</label>
                                                            <input readOnly value={selectedSurvey.branch?.address || ''} className={roInput} />
                                                        </div>
                                                        <div>
                                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Joint Visit</label>
                                                            <input readOnly value={selectedSurvey.jointVisit || ''} className={roInput} />
                                                        </div>
                                                        <div>
                                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Status</label>
                                                            {canEditRowStatus && isBankUser ? (
                                                                <div className="relative mt-1 min-h-[2.75rem] max-w-md">
                                                                    {rowSurveyStatusSavingId === selectedSurvey.id && (
                                                                        <div
                                                                            className="absolute inset-0 z-10 flex items-center justify-center rounded-xl border border-gray-200 bg-white/90 backdrop-blur-[1px] dark:border-gray-600 dark:bg-gray-900/90"
                                                                            aria-busy="true"
                                                                        >
                                                                            <DotLoader />
                                                                        </div>
                                                                    )}
                                                                    <select
                                                                        disabled={rowSurveyStatusSavingId === selectedSurvey.id || !companyId}
                                                                        value={normalizeDisplayStatus(selectedSurvey.status)}
                                                                        onChange={(e) => void handleSurveyStatusChange(selectedSurvey, e.target.value)}
                                                                        className="mt-1 w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                                                    >
                                                                        <option value="Pending">Pending</option>
                                                                        <option value="Resolved">Resolved</option>
                                                                        <option value="Approved">Approved</option>
                                                                        <option value="Rejected">Rejected</option>
                                                                    </select>
                                                                </div>
                                                            ) : canEditRowStatus && isEditing ? (
                                                                <select
                                                                    value={editedSurvey?.status || ''}
                                                                    onChange={(e) => setEditedSurvey({ ...editedSurvey, status: e.target.value })}
                                                                    className="mt-1 w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                                                >
                                                                    <option value="" className="bg-white text-gray-500 dark:bg-gray-800 dark:text-gray-400">Select Status...</option>
                                                                    <option value="Pending" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Pending</option>
                                                                    <option value="Resolved" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Resolved</option>
                                                                    <option value="Approved" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Approved</option>
                                                                    <option value="Rejected" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Rejected</option>
                                                                </select>
                                                            ) : (
                                                                <div className="mt-1">{getStatusBadge(selectedSurvey.status)}</div>
                                                            )}
                                                        </div>

                                                        {selectedSurvey.jointVisit === 'Yes' && toList((selectedSurvey as any).jointTeams).length ? (
                                                            <div className="sm:col-span-2">
                                                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Joint Teams</label>
                                                                <div className="mt-2 flex flex-wrap gap-2">
                                                                    {wrap((selectedSurvey as any).jointTeams)}
                                                                </div>
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                </div>

                                                 {/* Linked Complaints Section */}
                                                 {(() => {
                                                      if (loadingComplaints) {
                                                          return (
                                                              <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900/30">
                                                                  <h4 className="mb-4 text-base font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                                                      <Icon icon={FiAlertCircle} className="h-5 w-5 text-orange-500" />
                                                                      Complaints Detail
                                                                  </h4>
                                                                  <div className="flex flex-col items-center justify-center py-8 space-y-3">
                                                                      <DotLoader />
                                                                      <span className="text-sm font-semibold text-gray-500 dark:text-gray-400 animate-pulse">Loading complaints...</span>
                                                                  </div>
                                                              </div>
                                                          );
                                                      }

                                                      const surveyComplaints = complaints.filter(c => c.surveyId === selectedSurvey.id);
                                                      if (!surveyComplaints.length) return null;

                                                     const getHeaderType = (issueType?: string) => {
                                                         const lower = (issueType || '').toLowerCase();
                                                         if (lower.includes('cctv')) return 'CCTV';
                                                         if (lower.includes('bams')) return 'BAMS';
                                                         if (lower.includes('guard') || lower.includes('security')) return 'GUARDS';
                                                         if (lower.includes('other')) return 'OTHER';
                                                         return (issueType || 'GENERAL').toUpperCase();
                                                     };
                                                     
                                                     return (
                                                         <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900/30">
                                                             <h4 className="mb-4 text-base font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                                                 <Icon icon={FiAlertCircle} className="h-5 w-5 text-orange-500" />
                                                                 Complaints Detail
                                                             </h4>
                                                             <div className="space-y-6">
                                                                 {surveyComplaints.map((comp) => {
                                                                     const isCompResolved = normalizeDisplayStatus(comp.status) === 'Resolved';
                                                                     return (
                                                                         <div key={comp.id} className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 dark:border-gray-800 dark:bg-gray-900/40 relative">
                                                                             {rowComplaintStatusSavingId === comp.id && (
                                                                                 <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/70 backdrop-blur-[1px] dark:bg-gray-900/70" aria-busy="true">
                                                                                     <DotLoader />
                                                                                 </div>
                                                                             )}
                                                                             <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100 pb-3 mb-4 dark:border-gray-800">
                                                                                 <div>
                                                                                     <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-0.5">
                                                                                         Assessment Type: <span className="text-blue-600 dark:text-blue-400 font-extrabold">{getHeaderType(comp.issueType)}</span>
                                                                                     </div>
                                                                                     <span className="text-xs font-semibold text-gray-400">
                                                                                         ID: {comp.complaintId || comp.id}
                                                                                     </span>
                                                                                     <h5 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1">
                                                                                         {comp.issueType || 'General Issue'}
                                                                                     </h5>
                                                                                 </div>
                                                                                 <div className="flex items-center gap-3">
                                                                                     <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Status</span>
                                                                                     {canEditRowStatus && !isCompResolved ? (
                                                                                         <select
                                                                                             disabled={rowComplaintStatusSavingId === comp.id || !companyId}
                                                                                             value={normalizeDisplayStatus(comp.status)}
                                                                                             onChange={(e) => void handleComplaintStatusChange(comp, e.target.value)}
                                                                                             className="rounded-lg border-2 border-blue-500/20 bg-white px-3 py-1 text-xs font-semibold text-gray-900 shadow-sm outline-none transition focus:border-blue-500 dark:bg-gray-800 dark:text-gray-100 dark:border-blue-500/30"
                                                                                         >
                                                                                             {normalizeDisplayStatus(comp.status) === 'Partial' && <option value="Partial">Partial</option>}
                                                                                             <option value="Pending">Pending</option>
                                                                                             <option value="Resolved">Resolved</option>
                                                                                         </select>
                                                                                     ) : (
                                                                                         getStatusBadge(comp.status)
                                                                                     )}
                                                                                 </div>
                                                                             </div>
                                                                             
                                                                             {/* Issues checklist within the Survey Details Modal */}
                                                                             {comp.issues && comp.issues.length > 0 && (
                                                                                 <div className="space-y-3">
                                                                                     <div className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Issues Checklist</div>
                                                                                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                                                         {comp.issues.map((issue, idx) => {
                                                                                             const isResolved = comp.resolvedIssues?.includes(issue) || false;
                                                                                             return (
                                                                                                 <div key={`${issue}-${idx}`} className="flex items-start gap-3 rounded-xl border border-gray-100 bg-gray-50 p-3 transition dark:border-gray-800 dark:bg-gray-900/50">
                                                                                                     <label className="flex items-center gap-3 cursor-pointer w-full">
                                                                                                         <div className="relative flex h-5 w-5 shrink-0 items-center justify-center">
                                                                                                             <input
                                                                                                                 type="checkbox"
                                                                                                                 checked={isResolved}
                                                                                                                 disabled={!canEditRowStatus || rowComplaintStatusSavingId === comp.id}
                                                                                                                 onChange={(e) => void handleIssueToggle(comp, issue, e.target.checked)}
                                                                                                                 className="peer absolute h-5 w-5 cursor-pointer opacity-0 disabled:cursor-not-allowed"
                                                                                                             />
                                                                                                             <div className={`flex h-5 w-5 items-center justify-center rounded border-2 transition-colors peer-disabled:opacity-50 ${isResolved ? 'border-gray-900 bg-white dark:border-gray-100 dark:bg-transparent' : 'border-gray-400 bg-white dark:border-gray-600 dark:bg-transparent'}`}>
                                                                                                                 {isResolved && <Icon icon={FiCheck} className="h-3.5 w-3.5 text-gray-900 dark:text-gray-100" />}
                                                                                                             </div>
                                                                                                         </div>
                                                                                                         <span className={`text-sm font-medium ${isResolved ? 'text-gray-400 line-through dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}>
                                                                                                             {issue}
                                                                                                         </span>
                                                                                                     </label>
                                                                                                 </div>
                                                                                             );
                                                                                         })}
                                                                                     </div>
                                                                                 </div>
                                                                             )}
                                                                             
                                                                             {comp.description && (
                                                                                 <div className="mt-3 text-xs text-gray-600 dark:text-gray-400">
                                                                                     <span className="font-bold">Description: </span>{comp.description}
                                                                                 </div>
                                                                             )}
                                                                         </div>
                                                                     );
                                                                 })}
                                                             </div>
                                                         </div>
                                                     );
                                                 })()}
                                             </>
                                        );
                                    })()}
                                </>
                            )}
                            {activeTab === 'complaints' && selectedComplaint && (
                                <>
                                    {(() => {
                                        const roInput =
                                            'mt-1 w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 outline-none shadow-sm dark:border-gray-600 dark:bg-black/30 dark:text-gray-100';
                                        const roArea =
                                            'mt-1 w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-900 outline-none shadow-sm dark:border-gray-600 dark:bg-black/30 dark:text-gray-100';
                                        const chip =
                                            'rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-700 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-200';
                                        return (
                                            <>
                                                {/* Header Banner */}
                                                <div className="mb-6 rounded-2xl border border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 p-6 dark:border-gray-700 dark:from-gray-800 dark:to-gray-800/80">
                                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                                        <div>
                                                            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                                                Complaint {selectedComplaint.complaintId || selectedComplaint.id}
                                                            </h2>
                                                            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                                                                Related Survey: <span className="font-medium">{selectedComplaint.surveyData?.surveyId || 'N/A'}</span>
                                                            </p>
                                                        </div>
                                                        <div className="flex flex-col items-start md:items-end gap-2">
                                                            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">Current Status</div>
                                                            {canEditRowStatus && normalizeDisplayStatus(selectedComplaint.status) !== 'Resolved' ? (
                                                                <div className="relative min-w-[140px]">
                                                                    {rowComplaintStatusSavingId === selectedComplaint.id && (
                                                                        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl border border-gray-200 bg-white/90 backdrop-blur-[1px] dark:border-gray-600 dark:bg-gray-900/90" aria-busy="true">
                                                                            <DotLoader />
                                                                        </div>
                                                                    )}
                                                                    <select
                                                                        disabled={rowComplaintStatusSavingId === selectedComplaint.id || !companyId}
                                                                        value={normalizeDisplayStatus(selectedComplaint.status)}
                                                                        onChange={(e) => void handleComplaintStatusChange(selectedComplaint, e.target.value)}
                                                                        className="w-full rounded-xl border-2 border-blue-500/20 bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-blue-500/30"
                                                                    >
                                                                        {normalizeDisplayStatus(selectedComplaint.status) === 'Partial' && <option value="Partial">Partial</option>}
                                                                        <option value="Pending">Pending</option>
                                                                        <option value="Resolved">Resolved</option>
                                                                    </select>
                                                                </div>
                                                            ) : (
                                                                <div className="mt-1">{getStatusBadge(selectedComplaint.status)}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 mb-6">
                                                    {/* Details Card */}
                                                    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900/30">
                                                        <h3 className="mb-4 text-sm font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                                            <Icon icon={FiAlertCircle} className="h-4 w-4 text-blue-500" />
                                                            Complaint Information
                                                        </h3>
                                                        <div className="space-y-4">
                                                            <div>
                                                                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Issue Type</label>
                                                                <p className="mt-1 font-medium text-gray-900 dark:text-gray-100">{selectedComplaint.issueType || 'N/A'}</p>
                                                            </div>
                                                            <div>
                                                                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Date of Visit</label>
                                                                <p className="mt-1 font-medium text-gray-900 dark:text-gray-100">{selectedComplaint.surveyData?.dateOfVisit || 'N/A'}</p>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Branch Card */}
                                                    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900/30">
                                                        <h3 className="mb-4 text-sm font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                                            <Icon icon={FiClipboard} className="h-4 w-4 text-emerald-500" />
                                                            Location Details
                                                        </h3>
                                                        <div className="space-y-4">
                                                            <div>
                                                                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Branch Code</label>
                                                                <p className="mt-1 font-medium text-gray-900 dark:text-gray-100">{selectedComplaint.surveyData?.branch?.branchCode || 'N/A'}</p>
                                                            </div>
                                                            <div>
                                                                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Branch Address</label>
                                                                <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedComplaint.surveyData?.branch?.address || 'N/A'}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {selectedComplaint.issues?.length ? (
                                                    <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900/30">
                                                        <div className="mb-4 flex items-center justify-between">
                                                            <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                                                <Icon icon={FiAlertCircle} className="h-4 w-4 text-orange-500" />
                                                                Issues List
                                                            </h3>
                                                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                                                {selectedComplaint.resolvedIssues?.length || 0} of {selectedComplaint.issues.length} resolved
                                                            </span>
                                                        </div>
                                                        <div className="space-y-3">
                                                            {selectedComplaint.issues.map((issue, idx) => {
                                                                const isResolved = selectedComplaint.resolvedIssues?.includes(issue) || false;
                                                                return (
                                                                    <div key={`${issue}-${idx}`} className="flex items-start gap-3 rounded-xl border border-gray-100 bg-gray-50 p-3 transition dark:border-gray-800 dark:bg-gray-900/50">
                                                                        <label className="flex items-center gap-3 cursor-pointer w-full">
                                                                            <div className="relative flex h-5 w-5 shrink-0 items-center justify-center">
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={isResolved}
                                                                                    disabled={!canEditRowStatus || rowComplaintStatusSavingId === selectedComplaint.id}
                                                                                    onChange={(e) => void handleIssueToggle(selectedComplaint, issue, e.target.checked)}
                                                                                    className="peer absolute h-5 w-5 cursor-pointer opacity-0 disabled:cursor-not-allowed"
                                                                                />
                                                                                <div className={`flex h-5 w-5 items-center justify-center rounded border-2 transition-colors peer-disabled:opacity-50 ${isResolved ? 'border-gray-900 bg-white dark:border-gray-100 dark:bg-transparent' : 'border-gray-400 bg-white dark:border-gray-600 dark:bg-transparent'}`}>
                                                                                    {isResolved && <Icon icon={FiCheck} className="h-3.5 w-3.5 text-gray-900 dark:text-gray-100" />}
                                                                                </div>
                                                                            </div>
                                                                            <span className={`text-sm font-medium ${isResolved ? 'text-gray-400 line-through dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}>
                                                                                {issue}
                                                                            </span>
                                                                        </label>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                ) : null}
                                                {selectedComplaint.description ? (
                                                    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900/30">
                                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Description</label>
                                                        <textarea readOnly value={selectedComplaint.description} rows={3} className={roArea} />
                                                    </div>
                                                ) : null}
                                                {selectedComplaint.resolution ? (
                                                    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900/30">
                                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Resolution</label>
                                                        <textarea readOnly value={selectedComplaint.resolution} rows={3} className={roArea} />
                                                    </div>
                                                ) : null}
                                            </>
                                        );
                                    })()}
                                    {selectedComplaint.customIssues && selectedComplaint.customIssues.length > 0 && (
                                        <div className="mt-4">
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Custom Issues</label>
                                            <div className="mt-2 space-y-2">
                                                {selectedComplaint.customIssues.map((issue, index) => (
                                                    <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900">
                                                        <p className="font-medium text-gray-900 dark:text-gray-100">{issue.heading || 'Issue'}</p>
                                                        <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{issue.description || 'N/A'}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {selectedComplaint.description && (
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Description</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedComplaint.description}</p>
                                        </div>
                                    )}
                                    {selectedComplaint.resolution && (
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Resolution</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedComplaint.resolution}</p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                    </div>
                </div>
            )}

            {/* Email Modal */}
            {(selectedSurvey || selectedComplaint) && (
                <div className={`fixed inset-0 z-50 ${showEmailModal ? 'block' : 'hidden'}`}>
                    <div className="flex min-h-full items-center justify-center bg-black/50 p-4">
                        <div className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
                            <div className="mb-4 flex items-center justify-between">
                                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                    Send {activeTab === 'surveys' ? 'Survey' : 'Complaint'} Email
                                </h3>
                                <button
                                    onClick={() => {
                                        setShowEmailModal(false);
                                        setEmailRecipients('');
                                    }}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Email Addresses (comma, semicolon, or new line separated)
                                    </label>
                                    <textarea
                                        rows={3}
                                        placeholder="Enter email addresses separated by commas"
                                        value={emailRecipients}
                                        onChange={(e) => setEmailRecipients(e.target.value)}
                                        disabled={isSendingEmail}
                                        className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm outline-none transition text-gray-900 placeholder:text-gray-400 shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600"
                                    />
                                </div>
                                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
                                    <p className="text-xs text-blue-800 dark:text-blue-200">
                                        <strong>Info:</strong> {activeTab === 'surveys'
                                            ? `Survey ${selectedSurvey?.surveyId || 'N/A'}`
                                            : `Complaint ${selectedComplaint?.complaintId || selectedComplaint?.id || 'N/A'}`} will be sent to the recipients.
                                    </p>
                                </div>
                            </div>
                            <div className="mt-6 flex justify-end gap-3">
                                <Button variant="secondary" onClick={() => {
                                    setShowEmailModal(false);
                                    setEmailRecipients('');
                                }} disabled={isSendingEmail}>
                                    Cancel
                                </Button>
                                <Button onClick={handleSendEmail} disabled={isSendingEmail || !emailRecipients.trim()}>
                                    {isSendingEmail ? 'Sending...' : 'Send Email'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Survey Modal */}
            {showAddModal && (
                <Modal
                    isOpen={showAddModal}
                    onClose={() => {
                        setShowAddModal(false);
                        resetForm();
                    }}
                    title="New Branch Visit"
                    size="xl"
                >
                    <form onSubmit={handleAddSurvey} className="p-6">
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900/30 md:col-span-2">
                                    <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-gray-900 dark:text-gray-100">
                                        <Icon icon={FiCalendar} className="h-4 w-4" />
                                        Visit Information
                                    </h3>
                                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                        <div>
                                            <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-400">Date of Visit</label>
                                            <input
                                                type="date"
                                                value={formData.dateOfVisit}
                                                onChange={(e) => setFormData((p) => ({ ...p, dateOfVisit: e.target.value }))}
                                                required
                                                className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm outline-none transition shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600"
                                            />
                                        </div>

                                        <div className="relative" ref={branchDropdownRef}>
                                            <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-400">Branch Selection</label>
                                            <TextField
                                                value={
                                                    branchSearchTerm ||
                                                    (formData.branchId
                                                        ? (() => {
                                                              const b = branches.find((x) => x.id === formData.branchId);
                                                              if (!b) return '';
                                                              const code = b.branchCode || b.id;
                                                              const name = b.branchName || b.address || '';
                                                              return name ? `${code} - ${name}` : code;
                                                          })()
                                                        : '')
                                                }
                                                onChange={(e) => {
                                                    setBranchSearchTerm(e.target.value);
                                                    setShowBranchDropdown(true);
                                                    if (!e.target.value) {
                                                        setFormData((p) => ({ ...p, branchId: '' }));
                                                    }
                                                }}
                                                onFocus={() => setShowBranchDropdown(true)}
                                                placeholder="Click to select branch"
                                                prefix={<Icon icon={FiSearch} className="text-gray-400" />}
                                                required={!formData.branchId}
                                            />
                                        {showBranchDropdown && filteredBranches.length > 0 && (
                                            <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                                                {filteredBranches.map(branch => (
                                                    <button
                                                        key={branch.id}
                                                        type="button"
                                                        onClick={() => {
                                                            setFormData((p) => ({ ...p, branchId: branch.id }));
                                                            const code = branch.branchCode || branch.id;
                                                            const name = branch.branchName || branch.address || '';
                                                            setBranchSearchTerm(name ? `${code} - ${name}` : code);
                                                            setShowBranchDropdown(false);
                                                        }}
                                                        className={`w-full px-4 py-3 text-left text-sm transition hover:bg-blue-50 dark:hover:bg-gray-700 ${formData.branchId === branch.id ? 'bg-blue-100 dark:bg-blue-900/30' : ''}`}
                                                    >
                                                        <div className="font-medium text-gray-900 dark:text-gray-100">
                                                            {branch.branchCode || branch.id}
                                                            {(branch.branchName || branch.address) ? ` - ${branch.branchName || branch.address}` : ''}
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                        {showBranchDropdown && branchSearchTerm && filteredBranches.length === 0 && (
                                            <div className="absolute z-50 mt-1 w-full rounded-xl border border-gray-200 bg-white p-4 text-center text-sm text-gray-500 shadow-lg dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
                                                No branches found
                                            </div>
                                        )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* CCTV */}
                            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900/30">
                                <h3 className="mb-4 text-sm font-bold text-gray-900 dark:text-gray-100">CCTV System Assessment</h3>
                                <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-400">CCTV Condition</label>
                                <select
                                    value={formData.cctv.status}
                                    onChange={(e) => setFormData((p) => ({ ...p, cctv: { ...p.cctv, status: e.target.value as any } }))}
                                    className="mb-4 w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                                >
                                    <option value="Ok">Ok</option>
                                    <option value="Not OK">Not OK</option>
                                </select>
                                {formData.cctv.status === 'Not OK' && (
                                    <div className="space-y-4">
                                        <div>
                                            <div className="mb-2 text-xs font-semibold text-red-600 dark:text-red-400">Issues Identified</div>
                                            <div className="flex flex-wrap gap-2">
                                                {cctvIssueOptions.map((opt) => {
                                                    const selected = formData.cctv.issues.includes(opt);
                                                    return (
                                                        <button
                                                            key={opt}
                                                            type="button"
                                                            onClick={() =>
                                                                setFormData((p) => ({
                                                                    ...p,
                                                                    cctv: { ...p.cctv, issues: toggleInList(p.cctv.issues, opt) },
                                                                }))
                                                            }
                                                            className={`rounded-full border px-3 py-1 text-xs font-medium transition ${selected
                                                                ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-900/20 dark:text-red-200'
                                                                : 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200'
                                                                }`}
                                                        >
                                                            {opt}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="mb-2 text-xs font-semibold text-gray-700 dark:text-gray-200">Other Issues</div>
                                            <div className="flex gap-2">
                                                <input
                                                    value={(formData as any)._cctvOtherInput || ''}
                                                    onChange={(e) => setFormData((p: any) => ({ ...p, _cctvOtherInput: e.target.value }))}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            setFormData((p: any) => ({
                                                                ...p,
                                                                cctv: { ...p.cctv, otherIssues: addTag(p._cctvOtherInput || '', p.cctv.otherIssues) },
                                                                _cctvOtherInput: '',
                                                            }));
                                                        }
                                                    }}
                                                    placeholder="Type custom issue and press Add"
                                                    className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none transition shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600"
                                                />
                                                <Button
                                                    type="button"
                                                    onClick={() =>
                                                        setFormData((p: any) => ({
                                                            ...p,
                                                            cctv: { ...p.cctv, otherIssues: addTag(p._cctvOtherInput || '', p.cctv.otherIssues) },
                                                            _cctvOtherInput: '',
                                                        }))
                                                    }
                                                >
                                                    Add
                                                </Button>
                                            </div>
                                            {formData.cctv.otherIssues.length > 0 && (
                                                <div className="mt-2 flex flex-wrap gap-2">
                                                    {formData.cctv.otherIssues.map((t) => (
                                                        <button
                                                            key={t}
                                                            type="button"
                                                            onClick={() =>
                                                                setFormData((p) => ({
                                                                    ...p,
                                                                    cctv: { ...p.cctv, otherIssues: p.cctv.otherIssues.filter((x) => x !== t) },
                                                                }))
                                                            }
                                                            className="rounded-full bg-cyan-50 px-3 py-1 text-xs font-medium text-cyan-700 hover:bg-cyan-100 dark:bg-cyan-900/20 dark:text-cyan-200"
                                                        >
                                                            {t} <span className="ml-1">×</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <div className="mb-2 text-xs font-semibold text-gray-700 dark:text-gray-200">Remarks</div>
                                            <textarea
                                                value={formData.cctv.remarks}
                                                onChange={(e) => setFormData((p) => ({ ...p, cctv: { ...p.cctv, remarks: e.target.value } }))}
                                                rows={3}
                                                placeholder="Enter any additional remarks about CCTV issues..."
                                                className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none transition shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* BAMS */}
                            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900/30">
                                <h3 className="mb-4 text-sm font-bold text-gray-900 dark:text-gray-100">BAMS System Assessment</h3>
                                <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-400">BAMS Condition</label>
                                <select
                                    value={formData.bams.status}
                                    onChange={(e) => setFormData((p) => ({ ...p, bams: { ...p.bams, status: e.target.value as any } }))}
                                    className="mb-4 w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                                >
                                    <option value="Ok">Ok</option>
                                    <option value="Not OK">Not OK</option>
                                </select>
                                {formData.bams.status === 'Not OK' && (
                                    <div className="space-y-4">
                                        <div>
                                            <div className="mb-2 text-xs font-semibold text-red-600 dark:text-red-400">Issues Identified</div>
                                            <div className="flex flex-wrap gap-2">
                                                {bamsIssueOptions.map((opt) => {
                                                    const selected = formData.bams.issues.includes(opt);
                                                    return (
                                                        <button
                                                            key={opt}
                                                            type="button"
                                                            onClick={() => setFormData((p) => ({ ...p, bams: { ...p.bams, issues: toggleInList(p.bams.issues, opt) } }))}
                                                            className={`rounded-full border px-3 py-1 text-xs font-medium transition ${selected
                                                                ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-900/20 dark:text-red-200'
                                                                : 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200'
                                                                }`}
                                                        >
                                                            {opt}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="mb-2 text-xs font-semibold text-gray-700 dark:text-gray-200">Other Issues</div>
                                            <div className="flex gap-2">
                                                <input
                                                    value={(formData as any)._bamsOtherInput || ''}
                                                    onChange={(e) => setFormData((p: any) => ({ ...p, _bamsOtherInput: e.target.value }))}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            setFormData((p: any) => ({
                                                                ...p,
                                                                bams: { ...p.bams, otherIssues: addTag(p._bamsOtherInput || '', p.bams.otherIssues) },
                                                                _bamsOtherInput: '',
                                                            }));
                                                        }
                                                    }}
                                                    placeholder="Type custom issue and press Add"
                                                    className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none transition shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600"
                                                />
                                                <Button
                                                    type="button"
                                                    onClick={() =>
                                                        setFormData((p: any) => ({
                                                            ...p,
                                                            bams: { ...p.bams, otherIssues: addTag(p._bamsOtherInput || '', p.bams.otherIssues) },
                                                            _bamsOtherInput: '',
                                                        }))
                                                    }
                                                >
                                                    Add
                                                </Button>
                                            </div>
                                            {formData.bams.otherIssues.length > 0 && (
                                                <div className="mt-2 flex flex-wrap gap-2">
                                                    {formData.bams.otherIssues.map((t) => (
                                                        <button
                                                            key={t}
                                                            type="button"
                                                            onClick={() => setFormData((p) => ({ ...p, bams: { ...p.bams, otherIssues: p.bams.otherIssues.filter((x) => x !== t) } }))}
                                                            className="rounded-full bg-cyan-50 px-3 py-1 text-xs font-medium text-cyan-700 hover:bg-cyan-100 dark:bg-cyan-900/20 dark:text-cyan-200"
                                                        >
                                                            {t} <span className="ml-1">×</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <div className="mb-2 text-xs font-semibold text-gray-700 dark:text-gray-200">Remarks</div>
                                            <textarea
                                                value={formData.bams.remarks}
                                                onChange={(e) => setFormData((p) => ({ ...p, bams: { ...p.bams, remarks: e.target.value } }))}
                                                rows={3}
                                                placeholder="Enter any additional remarks about BAMS issues..."
                                                className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none transition shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Guarding */}
                            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900/30">
                                <h3 className="mb-4 text-sm font-bold text-gray-900 dark:text-gray-100">Guarding Assessment</h3>
                                <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-400">Guarding Condition</label>
                                <select
                                    value={formData.guarding.status}
                                    onChange={(e) => setFormData((p) => ({ ...p, guarding: { ...p.guarding, status: e.target.value as any } }))}
                                    className="mb-4 w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                                >
                                    <option value="Ok">Ok</option>
                                    <option value="Not OK">Not OK</option>
                                </select>
                                {formData.guarding.status === 'Not OK' && (
                                    <div className="space-y-4">
                                        <div>
                                            <div className="mb-2 text-xs font-semibold text-red-600 dark:text-red-400">Issues Identified</div>
                                            <div className="flex flex-wrap gap-2">
                                                {guardingIssueOptions.map((opt) => {
                                                    const selected = formData.guarding.issues.includes(opt);
                                                    return (
                                                        <button
                                                            key={opt}
                                                            type="button"
                                                            onClick={() => setFormData((p) => ({ ...p, guarding: { ...p.guarding, issues: toggleInList(p.guarding.issues, opt) } }))}
                                                            className={`rounded-full border px-3 py-1 text-xs font-medium transition ${selected
                                                                ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-900/20 dark:text-red-200'
                                                                : 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200'
                                                                }`}
                                                        >
                                                            {opt}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="mb-2 text-xs font-semibold text-gray-700 dark:text-gray-200">Other Issues</div>
                                            <div className="flex gap-2">
                                                <input
                                                    value={(formData as any)._guardOtherInput || ''}
                                                    onChange={(e) => setFormData((p: any) => ({ ...p, _guardOtherInput: e.target.value }))}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            setFormData((p: any) => ({
                                                                ...p,
                                                                guarding: { ...p.guarding, otherIssues: addTag(p._guardOtherInput || '', p.guarding.otherIssues) },
                                                                _guardOtherInput: '',
                                                            }));
                                                        }
                                                    }}
                                                    placeholder="Type custom issue and press Add"
                                                    className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none transition shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600"
                                                />
                                                <Button
                                                    type="button"
                                                    onClick={() =>
                                                        setFormData((p: any) => ({
                                                            ...p,
                                                            guarding: { ...p.guarding, otherIssues: addTag(p._guardOtherInput || '', p.guarding.otherIssues) },
                                                            _guardOtherInput: '',
                                                        }))
                                                    }
                                                >
                                                    Add
                                                </Button>
                                            </div>
                                            {formData.guarding.otherIssues.length > 0 && (
                                                <div className="mt-2 flex flex-wrap gap-2">
                                                    {formData.guarding.otherIssues.map((t) => (
                                                        <button
                                                            key={t}
                                                            type="button"
                                                            onClick={() => setFormData((p) => ({ ...p, guarding: { ...p.guarding, otherIssues: p.guarding.otherIssues.filter((x) => x !== t) } }))}
                                                            className="rounded-full bg-cyan-50 px-3 py-1 text-xs font-medium text-cyan-700 hover:bg-cyan-100 dark:bg-cyan-900/20 dark:text-cyan-200"
                                                        >
                                                            {t} <span className="ml-1">×</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <div className="mb-2 text-xs font-semibold text-gray-700 dark:text-gray-200">Remarks</div>
                                            <textarea
                                                value={formData.guarding.remarks}
                                                onChange={(e) => setFormData((p) => ({ ...p, guarding: { ...p.guarding, remarks: e.target.value } }))}
                                                rows={3}
                                                placeholder="Enter any additional remarks about Guarding issues..."
                                                className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none transition shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Other Assessments + Joint Visit */}
                            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900/30">
                                <h3 className="mb-4 text-sm font-bold text-gray-900 dark:text-gray-100">Other Assessments</h3>
                                <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-400">Other Assessments Condition</label>
                                <select
                                    value={formData.otherAssessments.status}
                                    onChange={(e) => setFormData((p) => ({ ...p, otherAssessments: { ...p.otherAssessments, status: e.target.value as any } }))}
                                    className="mb-4 w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                                >
                                    <option value="Ok">Ok</option>
                                    <option value="Not OK">Not OK</option>
                                </select>

                                {formData.otherAssessments.status === 'Not OK' && (
                                    <div className="space-y-4">
                                        <div>
                                            <div className="mb-2 text-xs font-semibold text-gray-700 dark:text-gray-200">Select Assessment Areas</div>
                                            <div className="flex flex-wrap gap-2">
                                                {Object.entries(otherAssessmentLabels).map(([key, label]) => {
                                                    const selected = (formData.otherAssessments.assessments as any)[key]?.selected;
                                                    return (
                                                        <button
                                                            key={key}
                                                            type="button"
                                                            onClick={() =>
                                                                setFormData((p) => ({
                                                                    ...p,
                                                                    otherAssessments: {
                                                                        ...p.otherAssessments,
                                                                        assessments: {
                                                                            ...p.otherAssessments.assessments,
                                                                            [key]: {
                                                                                ...(p.otherAssessments.assessments as any)[key],
                                                                                selected: !selected,
                                                                            },
                                                                        },
                                                                    },
                                                                }))
                                                            }
                                                            className={`rounded-full border px-3 py-1 text-xs font-medium transition ${selected
                                                                ? 'border-pink-300 bg-pink-50 text-pink-700 dark:border-pink-700 dark:bg-pink-900/20 dark:text-pink-200'
                                                                : 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200'
                                                                }`}
                                                        >
                                                            {label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {Object.entries(formData.otherAssessments.assessments).map(([key, v]) => {
                                            if (!v.selected) return null;
                                            const name = otherAssessmentLabels[key] || key;
                                            const opts = otherAssessmentIssueOptions[name] || [];
                                            return (
                                                <div key={key} className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/40">
                                                    <div className="mb-2 text-xs font-bold text-red-600 dark:text-red-400">{name} Issues</div>
                                                    <div className="flex flex-wrap gap-2">
                                                        {opts.map((opt) => {
                                                            const selected = v.issues.includes(opt);
                                                            return (
                                                                <button
                                                                    key={opt}
                                                                    type="button"
                                                                    onClick={() =>
                                                                        setFormData((p) => ({
                                                                            ...p,
                                                                            otherAssessments: {
                                                                                ...p.otherAssessments,
                                                                                assessments: {
                                                                                    ...p.otherAssessments.assessments,
                                                                                    [key]: {
                                                                                        ...(p.otherAssessments.assessments as any)[key],
                                                                                        issues: toggleInList((p.otherAssessments.assessments as any)[key].issues, opt),
                                                                                    },
                                                                                },
                                                                            },
                                                                        }))
                                                                    }
                                                                    className={`rounded-full border px-3 py-1 text-xs font-medium transition ${selected
                                                                        ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-900/20 dark:text-red-200'
                                                                        : 'border-gray-200 bg-white text-gray-700 dark:border-gray-700 dark:bg-gray-900/30 dark:text-gray-200'
                                                                        }`}
                                                                >
                                                                    {opt}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                    <div className="mt-3">
                                                        <div className="mb-2 text-xs font-semibold text-gray-700 dark:text-gray-200">Remarks</div>
                                                        <input
                                                            value={v.remarks}
                                                            onChange={(e) =>
                                                                setFormData((p) => ({
                                                                    ...p,
                                                                    otherAssessments: {
                                                                        ...p.otherAssessments,
                                                                        assessments: {
                                                                            ...p.otherAssessments.assessments,
                                                                            [key]: {
                                                                                ...(p.otherAssessments.assessments as any)[key],
                                                                                remarks: e.target.value,
                                                                            },
                                                                        },
                                                                    },
                                                                }))
                                                            }
                                                            placeholder={`Enter remarks for ${name}...`}
                                                            className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none transition shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600"
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        <div>
                                            <div className="mb-2 text-xs font-semibold text-gray-700 dark:text-gray-200">Other Issues</div>
                                            <div className="flex gap-2">
                                                <input
                                                    value={(formData as any)._otherOtherInput || ''}
                                                    onChange={(e) => setFormData((p: any) => ({ ...p, _otherOtherInput: e.target.value }))}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            setFormData((p: any) => ({
                                                                ...p,
                                                                otherAssessments: { ...p.otherAssessments, otherIssues: addTag(p._otherOtherInput || '', p.otherAssessments.otherIssues) },
                                                                _otherOtherInput: '',
                                                            }));
                                                        }
                                                    }}
                                                    placeholder="Type custom issue and press Add"
                                                    className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none transition shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600"
                                                />
                                                <Button
                                                    type="button"
                                                    onClick={() =>
                                                        setFormData((p: any) => ({
                                                            ...p,
                                                            otherAssessments: { ...p.otherAssessments, otherIssues: addTag(p._otherOtherInput || '', p.otherAssessments.otherIssues) },
                                                            _otherOtherInput: '',
                                                        }))
                                                    }
                                                >
                                                    Add
                                                </Button>
                                            </div>
                                            {formData.otherAssessments.otherIssues.length > 0 && (
                                                <div className="mt-2 flex flex-wrap gap-2">
                                                    {formData.otherAssessments.otherIssues.map((t) => (
                                                        <button
                                                            key={t}
                                                            type="button"
                                                            onClick={() => setFormData((p) => ({ ...p, otherAssessments: { ...p.otherAssessments, otherIssues: p.otherAssessments.otherIssues.filter((x) => x !== t) } }))}
                                                            className="rounded-full bg-cyan-50 px-3 py-1 text-xs font-medium text-cyan-700 hover:bg-cyan-100 dark:bg-cyan-900/20 dark:text-cyan-200"
                                                        >
                                                            {t} <span className="ml-1">×</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <div className="mb-2 text-xs font-semibold text-gray-700 dark:text-gray-200">Remarks</div>
                                            <textarea
                                                value={formData.otherAssessments.remarks}
                                                onChange={(e) => setFormData((p) => ({ ...p, otherAssessments: { ...p.otherAssessments, remarks: e.target.value } }))}
                                                rows={3}
                                                placeholder="Enter any additional remarks about Other Assessments..."
                                                className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none transition shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900/30">
                                <h3 className="mb-4 text-sm font-bold text-gray-900 dark:text-gray-100">Joint Visit Information</h3>
                                <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-400">Joint Visit</label>
                                <select
                                    value={formData.jointVisit}
                                    onChange={(e) => setFormData((p) => ({ ...p, jointVisit: e.target.value as any, jointTeams: e.target.value === 'Yes' ? p.jointTeams : [] }))}
                                    className="mb-4 w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                                >
                                    <option value="No">No</option>
                                    <option value="Yes">Yes</option>
                                </select>

                                {formData.jointVisit === 'Yes' && (
                                    <div>
                                        <div className="mb-2 text-xs font-semibold text-gray-700 dark:text-gray-200">Select Joint Teams</div>
                                        <div className="flex flex-wrap gap-2">
                                            {jointOptions.map((opt) => {
                                                const selected = formData.jointTeams.includes(opt);
                                                return (
                                                    <button
                                                        key={opt}
                                                        type="button"
                                                        onClick={() => setFormData((p) => ({ ...p, jointTeams: toggleInList(p.jointTeams, opt) }))}
                                                        className={`rounded-full border px-3 py-1 text-xs font-medium transition ${selected
                                                            ? 'border-cyan-300 bg-cyan-50 text-cyan-700 dark:border-cyan-700 dark:bg-cyan-900/20 dark:text-cyan-200'
                                                            : 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200'
                                                            }`}
                                                    >
                                                        {opt}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mt-6 flex justify-end gap-3">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => {
                                    setShowAddModal(false);
                                    resetForm();
                                }}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={isSubmitting || !formData.branchId}
                            >
                                {isSubmitting ? 'Saving Survey...' : '💾 Save Visit Survey'}
                            </Button>
                        </div>
                    </form>
                </Modal>
            )}
            {exportProgress.active && (
                <div className="pointer-events-auto fixed bottom-4 left-4 z-[100] w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 shadow-2xl shadow-slate-900/10 backdrop-blur-md dark:border-slate-600 dark:bg-slate-900/95">
                    <div className="border-b border-slate-100 bg-gradient-to-r from-slate-800 to-slate-700 px-4 py-3 dark:from-slate-900 dark:to-slate-800">
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 text-white">
                                <Icon icon={FiDownload} className="h-4 w-4 opacity-90" />
                                <span className="text-sm font-semibold tracking-tight">
                                    {exportProgress.format === 'excel' ? 'Exporting Excel' : 'Exporting PDF'}
                                </span>
                            </div>
                            <span className="text-sm font-bold tabular-nums text-cyan-300">{Math.round(exportProgress.percent)}%</span>
                        </div>
                    </div>
                    <div className="space-y-3 p-4">
                        <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-300">{exportProgress.message}</p>
                        <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                            <div
                                className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 transition-[width] duration-300 ease-out"
                                style={{ width: `${Math.min(100, Math.max(2, exportProgress.percent))}%` }}
                            />
                        </div>
                        <p className="flex items-start gap-2 text-xs font-medium text-amber-800 dark:text-amber-200/90">
                            <Icon icon={FiAlertCircle} className="mt-0.5 h-4 w-4 shrink-0" />
                            <span>Do not close this tab or log out until the export finishes. The browser may ask you to confirm if you try to leave.</span>
                        </p>
                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={cancelOngoingExport}
                                className="text-xs font-semibold text-slate-500 underline decoration-slate-300 underline-offset-2 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                            >
                                Request cancel (after current step)
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

